import { defineStore } from 'pinia';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { notify } from 'notiwind';
import { generateUUID } from '@/utils/uuid.js';
import { parseLocalDate } from '@/utils/dateUtils';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(quarterOfYear);

const _filterDebounceTimers = {};

// Map raw DB benefit categories to the higher-level groups shown in Figma
// (used by both the Current and Insights tab Category filters). Anything
// unmapped falls through to 'lifestyle'.
//
// SUPPORT-757: that fallback is silent, so every category slug MUST have an
// entry here — a missing key does not error, it just quietly files the benefit
// under Lifestyle. `travel_credit` was unmapped, which is how "$300 Annual
// Travel Credit" ended up outside Travel.
//
// Slugs reach this map from two places, and only the first is enumerable:
//
//   1. Config. config/benefitclassification.php ('default_category') and
//      config/benefit_categories.php ('category_merchant_patterns'). The
//      support-757 test derives these and fails on any that land here
//      unmapped.
//   2. The admin benefit forms. card-benefit-creator.vue,
//      card-benefit-editor.vue and card-benefits-bulk-editor.vue each ship a
//      hardcoded <option> list, and the three do not agree with each other
//      (only 20 options are common to all of them). The support-757 test
//      derives these too.
//   3. The AI review job. ProcessAIBenefitsReview writes
//      $aiResponse['category'] straight to the column with no allowlist (no
//      validateCategory exists, unlike its validateBenefitType /
//      validateResetBasis siblings), so the model can invent slugs no config
//      declares. `airline_credit`, `hotel_credit`, `lounge_access`,
//      `dining_credit`, `streaming_credit`, `grocery`, `shopping_credit`,
//      `subscription`, `anniversary_bonus` and `cashback` all reached prod
//      this way — several of them reinventing a canonical slug that already
//      existed. No static test can catch the next one; that needs a
//      server-side allowlist.
//
// Historically the vocabulary was closed: config/benefitclassification.php
// carried a `category_hints` regex block, applied by applyCategoryHints(),
// which normalised benefit text into ~9 canonical categories and bottomed out
// at 'other'. The AI category-refinement prompt landed 2025-10-16 (8265361fa0)
// and applyCategoryHints() was removed 2025-10-20 (973f581831), which is when
// the set stopped being closed.
export const BENEFIT_CATEGORY_MAP = {
    dining: 'dining',
    dining_credit: 'dining',
    travel: 'travel',
    airline: 'travel',
    travel_portal: 'travel',
    travel_credit: 'travel',
    airline_credit: 'travel',
    transfer_points: 'travel',
    rideshare: 'travel',
    car_rental: 'travel',
    no_foreign_fees: 'travel',
    checked_bag: 'travel',
    seat_upgrade: 'travel',
    hotel: 'hotels_stays',
    hotel_credit: 'hotels_stays',
    entertainment: 'entertainment_streaming',
    streaming_credit: 'entertainment_streaming',
    streaming: 'entertainment_streaming',
    lounge: 'travel_entry_lounge',
    lounge_access: 'travel_entry_lounge',
    tsa: 'travel_entry_lounge',
    priority_boarding: 'travel_entry_lounge',
    companion_certificate: 'travel_entry_lounge',
    concierge: 'travel_entry_lounge',
    status: 'travel_entry_lounge',
    fitness: 'lifestyle',
    retail: 'lifestyle',
    financial: 'lifestyle',
    discount: 'lifestyle',
    rewards: 'lifestyle',
    gas: 'lifestyle',
    cell_phone_insurance: 'lifestyle',
    cell_phone_protection: 'lifestyle',
    insurance: 'lifestyle',
    auto: 'lifestyle',
    roadside_assistance: 'lifestyle',
    rental_car_insurance: 'lifestyle',
    trip_insurance: 'lifestyle',
    fraud_protection: 'lifestyle',
    authorized_user: 'lifestyle',
    technology: 'lifestyle',
    metal_card: 'lifestyle',
    website: 'lifestyle',
    grocery: 'lifestyle',
    shopping_credit: 'lifestyle',
    // Mixed bucket, and the slug describes a billing model rather than a
    // category. Its rows are Instacart/DashPass, fraud protection, software
    // and household perks. Lifestyle is the least-wrong single destination --
    // grocery, fraud_protection and technology all map here too -- but it is
    // not right for all of them: a DashPass credit is DoorDash, which the
    // dining patterns in config/benefit_categories.php would classify as
    // dining. No mapping can reach that while the stored category says
    // `subscription`; those rows need re-classifying at the source, which an
    // admin edit on a locked version will hold (ProcessAIBenefitsReview skips
    // locked versions rather than overwriting them).
    subscription: 'lifestyle',
    anniversary_bonus: 'lifestyle',
    cashback: 'lifestyle',
    business: 'lifestyle',
    credit: 'lifestyle',
    membership: 'lifestyle',
    protection: 'lifestyle',
    service: 'lifestyle',
    shipping: 'lifestyle',
    warranty: 'lifestyle',
    wireless_services: 'lifestyle',
    other: 'lifestyle',
};

const BENEFIT_CATEGORY_LABELS = {
    dining: 'Dining',
    travel: 'Travel',
    hotels_stays: 'Hotels & Stays',
    entertainment_streaming: 'Entertainment & Streaming',
    lifestyle: 'Lifestyle',
    travel_entry_lounge: 'Travel Entry & Lounge',
};

export const useCardBenefitsStore = defineStore('cardBenefits', {
    state: () => ({
        // Core data - unified structure from backend
        benefits: [],
        totalCount: 0,
        loading: false,
        error: null,
        hasError: false,
        benefitsByCard: null,
        // Monotonic counter used by fetchBenefits to guard its deferred
        // loading-clear against races between back-to-back calls.
        fetchId: 0,

        benefitUsageSummary: null,

        // Freemium state
        //
        // `freemiumLoaded` starts false and flips to true only after the
        // /wallets/{id}/card-benefits response has populated the block. The
        // upgrade banner reads this flag to distinguish "membership status
        // unresolved" from "confirmed free" — without it, the banner falsely
        // renders during the loading interval for every user because
        // `isSubscribed` defaults to false. (SUPPORT-722)
        freemiumLoaded: false,
        freemium: {
            isSubscribed: false,
            trackedConnectedAccountIds: [],
            maxTrackedCards: 1,
            availableCards: [],
            walletUsersCount: 1,
            walletUsers: [],
            bannerDismissed: false,
            monthlySwitchesUsed: 0,
            maxMonthlySwitches: 3,
        },

        // Sidebar/modal state
        sidebarOpen: false,
        selectedBenefit: null,
        walletId: null,
        showLastFour: true,
        showInactiveBenefits: true,
        showHiddenBenefits: false,
        // MY1-3082 / MY1-3083: session-side memory for the benefit details
        // prompts. Connect-bank dismissals also persist per institution on the
        // user (dismissed_connect_institutions, shared with the wallet's
        // banner); the My Spending prompt is dismissed for the session only.
        dismissedConnectInstitutions: [],
        spendingPromptDismissed: false,
        spendingOptInBusy: false,
        // MY1-3080: the "Add from My Spending" transaction picker, mounted once
        // on the page and opened from any surface's Add usage menu.
        transactionPicker: {
            open: false,
            benefit: null,
            remainingAmount: 0,
        },
        showAddUsageModal: false,
        showEditUsageModal: false,
        showSelectCardsModal: false,
        showUpgradeModal: false,

        // Filter state
        selectedCard: null,
        selectedUsage: 'all',
        searchQuery: '',
        sortBy: 'benefit',
        sortDirection: 'asc',
        selectedCards: [],
        selectedUsageTypes: [],
        selectedUsers: [],
        selectedBanks: [],
        selectedPeriodTypes: [],
        selectedCategories: [],

        // View configuration
        viewMode: 'flat', // 'flat', 'by_card'
        sortConfig: { column: 'benefit', direction: 'asc' },

        // Column configuration - consistent across all view modes
        columns: [
            {
                key: 'benefit',
                label: 'Benefit',
                sortable: true,
                show: true,
                width: 'w-96 grow',
            },
            {
                key: 'user',
                label: 'User',
                sortable: true,
                show: true,
                width: 'w-32 grow',
            },
            {
                key: 'value_up_to',
                label: 'Value up to',
                sortable: true,
                show: true,
                width: 'w-32 grow',
            },
            {
                key: 'used_current_period',
                label: 'Used current period',
                sortable: true,
                show: true,
                width: 'w-64 grow',
            },
            {
                key: 'expires_in',
                label: 'Expires in',
                sortable: true,
                show: true,
                width: 'w-32 grow',
            },
        ],

        // Insights tab state (MY1-2046)
        insightsActiveTab: 'current', // 'current' | 'insights'
        // Multi-select filters mirror the Current tab pattern.
        insightsSelectedCards: [],
        insightsSelectedBanks: [],
        insightsSelectedPeriodTypes: [],
        insightsSelectedUsage: [], // [{ id: 'used' | 'unused', name: 'Used' | 'Unused' }]
        insightsSelectedCategories: [], // [{ id: 'dining' | 'travel' | ..., name: 'Dining' | ... }]
        insightsSelectedUsers: [], // [{ id, name }] — only relevant when the wallet has multiple users
        // Display popover state for the Insights table.
        // `insightsGroupBy` mirrors the Current tab's `viewMode` ('flat' / 'by_card');
        // `insightsShowCardNumbers` toggles the `(last4)` suffix throughout Insights.
        insightsGroupBy: 'flat',
        insightsShowCardNumbers: true,
        // Date-range chooser. `insightsRangePreset` is one of:
        //   'this-year'      Jan 1 through end of current month
        //   'previous-year'  Jan 1 - Dec 31 of the prior calendar year
        //   'this-month'     Current calendar month
        //   'previous-month' Prior calendar month
        //   'custom'         User-defined start/end stored on insightsCustomRange
        // Shifts move the window back a full preset-length step at a time
        // (1 year for *-year presets, 1 month for *-month presets, the full
        // window length for 'custom').
        insightsRangePreset: 'this-year',
        insightsRangeShiftCount: 0,
        insightsCustomRange: null,
        // Mobile-only sort selector for the Insights benefits list. Values:
        //   'benefit_asc'       — alphabetical by benefit title (default)
        //   'used_desc' / 'used_asc'
        //   'unused_desc' / 'unused_asc'
        //   'utilization_desc' / 'utilization_asc'
        insightsSortBy: 'benefit_asc',
        // Whether the "Usage by card" widget is showing all rows (true) or
        // collapsed to the first 3 + a teaser (false). Lifted to the store so
        // sibling widgets (e.g. "Redeemed by card") can opt out of stretching
        // when only Usage by card grows past the shared row height.
        usageByCardExpanded: false,
    }),

    getters: {
        // Freemium getters
        isSubscribed(state) {
            return state.freemium.isSubscribed;
        },

        trackedCardIds(state) {
            return state.freemium.trackedConnectedAccountIds;
        },

        maxTrackedCards(state) {
            return state.freemium.maxTrackedCards;
        },

        availableCardsForTracking(state) {
            return state.freemium.availableCards;
        },

        trackedCardsCount(state) {
            return state.freemium.trackedConnectedAccountIds.length;
        },

        bannerDismissed(state) {
            return state.freemium.bannerDismissed;
        },

        walletUsersCount(state) {
            return state.freemium.walletUsersCount;
        },

        walletUsers(state) {
            return state.freemium.walletUsers;
        },

        // Check if a benefit is tracked (by connected_account_id)
        isBenefitTracked: (state) => (connectedAccountId) => {
            if (state.freemium.isSubscribed) return true;
            return state.freemium.trackedConnectedAccountIds.includes(
                connectedAccountId
            );
        },

        // Check if freemium user has tracked a card with no benefits
        trackedCardHasNoBenefits(state) {
            if (state.freemium.isSubscribed) return false;
            if (state.freemium.trackedConnectedAccountIds.length === 0)
                return false;

            // Check if any benefits exist for the tracked cards
            const trackedIds = state.freemium.trackedConnectedAccountIds;
            const hasBenefits = state.benefits.some((b) =>
                trackedIds.includes(b.connected_account_id)
            );
            return !hasBenefits;
        },

        // Get the name of the tracked card (for display in empty state)
        trackedCardName(state) {
            if (state.freemium.trackedConnectedAccountIds.length === 0)
                return null;
            const trackedId = state.freemium.trackedConnectedAccountIds[0];
            const card = state.freemium.availableCards.find(
                (c) => c.connected_account_id === trackedId
            );
            return card ? card.name : null;
        },

        filteredColumns(state) {
            let copy;

            if (this.availableUsers.length <= 1) {
                copy = state.columns.filter((col) => col.key !== 'user');
            } else {
                copy = state.columns;
            }
            if (state.viewMode === 'by_card') {
                copy = copy.filter((col) => col.key !== 'value_up_to');
            }
            return copy;
        },

        filteredBenefits(state) {
            // Helper to apply usage type and user filters (excl. card filters)
            const applyNonCardFilters = (array) => {
                let filtered = array;

                if (state.selectedUsageTypes.length > 0) {
                    const { start: ytdStart, end: ytdEnd } =
                        this.periodBoundaries.annual;

                    filtered = filtered.filter((item) => {
                        const hasYtdUsageRecords = item.usage_records?.some(
                            (r) => {
                                const usedAt = dayjs(r.used_at);
                                return (
                                    usedAt.isSameOrAfter(ytdStart) &&
                                    usedAt.isSameOrBefore(ytdEnd)
                                );
                            }
                        );

                        return state.selectedUsageTypes.some((usageType) => {
                            if (usageType.id === 'redeemed') {
                                return hasYtdUsageRecords;
                            } else if (usageType.id === 'not_redeemed') {
                                return !hasYtdUsageRecords;
                            }
                            return false;
                        });
                    });
                }

                if (state.selectedUsers.length > 0) {
                    filtered = filtered.filter((item) =>
                        state.selectedUsers.some(
                            (user) => user.id === item.user?.id
                        )
                    );
                }

                if (state.selectedBanks.length > 0) {
                    filtered = filtered.filter((item) =>
                        state.selectedBanks.some(
                            (bank) => bank.id === item.card?.issuing_bank_id
                        )
                    );
                }

                if (state.selectedPeriodTypes.length > 0) {
                    filtered = filtered.filter((item) =>
                        state.selectedPeriodTypes.some(
                            (periodType) =>
                                periodType.id?.toLowerCase() ===
                                item.benefit?.period_type?.toLowerCase()
                        )
                    );
                }

                if (state.selectedCategories.length > 0) {
                    filtered = filtered.filter((item) => {
                        const raw = item.benefit?.category || 'other';
                        const groupId =
                            BENEFIT_CATEGORY_MAP[raw] || 'lifestyle';
                        return state.selectedCategories.some(
                            (c) => c.id === groupId
                        );
                    });
                }

                return filtered;
            };

            if (state.viewMode === 'flat') {
                let filtered = state.benefits;

                // Filter out inactive benefits when toggle is OFF
                if (!state.showInactiveBenefits) {
                    filtered = filtered.filter(
                        (item) => item.is_active !== false
                    );
                }

                // Filter out user-hidden benefits when toggle is OFF
                if (!state.showHiddenBenefits) {
                    filtered = filtered.filter(
                        (item) => item.user_override?.is_hidden !== true
                    );
                }

                // For freemium users, mark non-tracked benefits as locked (don't filter them out)
                if (
                    !state.freemium.isSubscribed &&
                    state.freemium.trackedConnectedAccountIds.length > 0
                ) {
                    filtered = filtered.map((item) => ({
                        ...item,
                        isLocked:
                            !state.freemium.trackedConnectedAccountIds.includes(
                                item.connected_account_id
                            ),
                    }));
                } else {
                    // Subscribed users or no tracked cards: nothing is locked
                    filtered = filtered.map((item) => ({
                        ...item,
                        isLocked: false,
                    }));
                }

                // Apply card filter
                if (state.selectedCards.length > 0) {
                    filtered = filtered.filter((item) =>
                        state.selectedCards.some(
                            (card) =>
                                card.connected_account_id ===
                                item.connected_account_id
                        )
                    );
                }

                // Apply usage type, user, and bank filters
                filtered = applyNonCardFilters(filtered);

                // Apply search filter
                if (state.searchQuery) {
                    const q = state.searchQuery.toLowerCase();
                    filtered = filtered.filter((item) => {
                        const name = (
                            item.benefit?.benefit_title || ''
                        ).toLowerCase();
                        const card = (item.card?.title || '').toLowerCase();
                        const bank = (
                            item.card?.issuing_bank_name || ''
                        ).toLowerCase();
                        return (
                            name.includes(q) ||
                            card.includes(q) ||
                            bank.includes(q)
                        );
                    });
                }

                return this.applySorting(filtered);
            }

            if (state.viewMode === 'by_card') {
                let groups = state.benefitsByCard?.groups || [];

                // Filter out inactive benefits when toggle is OFF
                if (!state.showInactiveBenefits) {
                    groups = groups.map((group) => ({
                        ...group,
                        benefits: group.benefits.filter(
                            (item) => item.is_active !== false
                        ),
                    }));
                    // Remove groups with no remaining benefits
                    groups = groups.filter(
                        (group) => group.benefits.length > 0
                    );
                }

                // Filter out user-hidden benefits when toggle is OFF
                if (!state.showHiddenBenefits) {
                    groups = groups.map((group) => ({
                        ...group,
                        benefits: group.benefits.filter(
                            (item) => item.user_override?.is_hidden !== true
                        ),
                    }));
                    groups = groups.filter(
                        (group) => group.benefits.length > 0
                    );
                }

                // For freemium users, mark non-tracked groups as locked (don't filter them out)
                if (
                    !state.freemium.isSubscribed &&
                    state.freemium.trackedConnectedAccountIds.length > 0
                ) {
                    groups = groups.map((group) => ({
                        ...group,
                        isLocked:
                            !state.freemium.trackedConnectedAccountIds.includes(
                                group.key
                            ),
                        benefits: group.benefits.map((benefit) => ({
                            ...benefit,
                            isLocked:
                                !state.freemium.trackedConnectedAccountIds.includes(
                                    group.key
                                ),
                        })),
                    }));
                    // Sort: tracked cards first, then locked cards
                    groups = groups.sort((a, b) => {
                        if (a.isLocked === b.isLocked) return 0;
                        return a.isLocked ? 1 : -1;
                    });
                } else {
                    // Subscribed users: nothing is locked
                    groups = groups.map((group) => ({
                        ...group,
                        isLocked: false,
                        benefits: group.benefits.map((benefit) => ({
                            ...benefit,
                            isLocked: false,
                        })),
                    }));
                }

                // Apply card filtering at group level
                if (state.selectedCards.length > 0) {
                    groups = groups.filter((group) => {
                        return state.selectedCards.some(
                            (card) =>
                                card.id === group.key ||
                                card.connected_account_id === group.key
                        );
                    });
                }

                // Apply bank filtering at group level
                if (state.selectedBanks.length > 0) {
                    groups = groups.filter((group) => {
                        return state.selectedBanks.some(
                            (bank) => bank.id === group.card?.issuing_bank_id
                        );
                    });
                }

                // Apply usage type and user filters to each group's benefits array
                // Note: Card filtering is already done at group level above
                groups = groups.map((group) => {
                    const filteredBenefits = applyNonCardFilters(
                        group.benefits
                    );
                    return {
                        ...group,
                        benefits: this.applySorting(filteredBenefits),
                    };
                });

                // Filter out groups that have no benefits after filtering
                groups = groups.filter((group) => group.benefits.length > 0);

                // Apply search filter within each group's benefits
                if (state.searchQuery) {
                    const q = state.searchQuery.toLowerCase();
                    groups = groups.map((group) => ({
                        ...group,
                        benefits: group.benefits.filter((item) => {
                            const name = (
                                item.benefit?.benefit_title || ''
                            ).toLowerCase();
                            const card = (item.card?.title || '').toLowerCase();
                            const bank = (
                                item.card?.issuing_bank_name || ''
                            ).toLowerCase();
                            return (
                                name.includes(q) ||
                                card.includes(q) ||
                                bank.includes(q)
                            );
                        }),
                    }));
                    groups = groups.filter(
                        (group) => group.benefits.length > 0
                    );
                }

                return groups;
            }

            return [];
        },

        // Flatten all benefits for navigation and summary calculation purposes
        flattenedBenefits(state) {
            if (state.viewMode === 'flat') {
                return this.filteredBenefits;
            }

            // For grouped views, flatten benefits from filtered groups but resolve
            // each to its live counterpart in state.benefits. This ensures summary
            // totals stay in sync with individual table rows after optimistic updates
            // (state.benefits is updated optimistically, but benefitsByCard is not).
            const flattened = [];
            if (this.filteredBenefits && Array.isArray(this.filteredBenefits)) {
                this.filteredBenefits.forEach((group) => {
                    if (group.benefits && Array.isArray(group.benefits)) {
                        group.benefits.forEach((groupBenefit) => {
                            const liveBenefit =
                                state.benefits.find(
                                    (b) =>
                                        b.benefit?.id ===
                                            groupBenefit.benefit?.id &&
                                        b.connected_account_id ===
                                            groupBenefit.connected_account_id
                                ) || groupBenefit;
                            flattened.push(liveBenefit);
                        });
                    }
                });
            }
            return flattened;
        },

        // Current benefit index in flattened list (0-based)
        currentBenefitIndex(state) {
            if (!state.selectedBenefit || !this.flattenedBenefits.length) {
                return 0;
            }
            const index = this.flattenedBenefits.findIndex(
                (benefit) =>
                    benefit.benefit.id === state.selectedBenefit.benefit.id &&
                    benefit.connected_account_id ===
                        state.selectedBenefit.connected_account_id
            );
            return index >= 0 ? index : 0;
        },

        // Current benefit position for display (1-based)
        currentBenefitPosition() {
            return this.currentBenefitIndex + 1;
        },

        // Total benefits count
        totalBenefitsCount() {
            return this.flattenedBenefits.length;
        },

        // Navigation state
        isFirstBenefit() {
            return this.currentBenefitIndex <= 0;
        },

        isLastBenefit() {
            return this.currentBenefitIndex >= this.totalBenefitsCount - 1;
        },

        availableBenefitCategories: (state) => [
            ...new Set(
                state.benefits.map((b) => b.benefit_category).filter(Boolean)
            ),
        ],

        availableUsers: (state) => [
            ...new Set(state.benefits.map((b) => b.user?.name).filter(Boolean)),
        ],

        hasActiveFilters(state) {
            return (
                state.selectedCards.length > 0 ||
                state.selectedUsageTypes.length > 0 ||
                state.selectedUsers.length > 0 ||
                state.selectedBanks.length > 0 ||
                state.selectedPeriodTypes.length > 0 ||
                state.selectedCategories.length > 0 ||
                state.searchQuery !== ''
            );
        },

        /**
         * Helper to calculate total annual value for a given benefits array
         * Used by both totalAnnualValue and filteredTotalAnnualValue
         */
        _calculateTotalAnnualValueForBenefits() {
            return (benefits) => {
                let total = 0;
                const { start: ytdStart, end: ytdEnd } =
                    this.periodBoundaries.annual;

                for (const benefitItem of benefits) {
                    const benefit = benefitItem.benefit;
                    if (!benefit) continue;

                    // Skip count-only benefits and NCRED (non-dollar) benefits
                    if (this.checkIsCountBased(benefit)) {
                        continue;
                    }

                    // Skip multi-year benefits that were used in a previous year
                    if (
                        !this.isBenefitActiveThisYear(
                            benefitItem,
                            ytdStart,
                            ytdEnd
                        )
                    ) {
                        continue;
                    }

                    const annualValue = this.calculateAnnualValue(benefitItem);
                    total += annualValue;
                }

                return total;
            };
        },

        /**
         * Calculate total annual value for all user's benefits
         * Handles: multi-year benefits, first-year values, monthly overrides,
         * count-based benefits, and eligible_after_first_anniversary
         */
        totalAnnualValue(state) {
            return this._calculateTotalAnnualValueForBenefits(state.benefits);
        },

        /**
         * Calculate percentage of benefits used
         */
        percentUsed() {
            const total = this.totalAnnualValue;
            const used = this.benefitUsageSummary?.used_ytd || 0;

            if (total === 0) return 0;

            return Math.round((used / total) * 100 * 10) / 10; // Round to 1 decimal
        },

        /**
         * Complete benefit usage summary with frontend-calculated values
         */
        completeBenefitUsageSummary(state) {
            return {
                total_annual_value: this.totalAnnualValue,
                used_ytd: state.benefitUsageSummary?.used_ytd || 0,
                percent_used: this.percentUsed,
            };
        },

        /**
         * Check if all filtered benefits are purely count-based (no dollar value)
         * Used to determine whether to display counts vs dollars in summary stats
         */
        filteredAllPurelyCountBased(state) {
            const benefitsToCheck = this.hasActiveFilters
                ? this.flattenedBenefits
                : state.benefits;

            if (benefitsToCheck.length === 0) return false;

            return benefitsToCheck.every((item) =>
                this.checkIsCountBased(item.benefit)
            );
        },

        /**
         * Calculate total annual value from filtered benefits
         * When filters are active, only includes filtered benefits
         * When no filters, includes all benefits (same as totalAnnualValue)
         * For purely count-based benefits, returns total available count instead
         */
        filteredTotalAnnualValue(state) {
            const benefitsToSum = this.hasActiveFilters
                ? this.flattenedBenefits
                : state.benefits;

            // If all benefits are purely count-based, return total count
            if (this.filteredAllPurelyCountBased) {
                return benefitsToSum.reduce((sum, item) => {
                    const count =
                        parseFloat(item.benefit?.value_details?.count) || 0;
                    return sum + count;
                }, 0);
            }

            return this._calculateTotalAnnualValueForBenefits(benefitsToSum);
        },

        /**
         * Calculate used YTD from filtered benefits.
         * When filters are active, only sums YTD usage from filtered benefits.
         * When no filters, sums all usage (matches backend used_ytd).
         *
         * Uses per-benefit `used_ytd` (calendar-year YTD usage, including
         * sub-benefit records for compound benefits — SUPPORT-537) rather than
         * `current_period_usage`, since the latter only reflects the current
         * monthly/quarterly window and would drop earlier-in-year credits.
         *
         * Applies the same benefit exclusion logic as _calculateTotalAnnualValueForBenefits:
         * - Skips purely count-based benefits (no dollar amount) in mixed scenarios
         * - Skips multi-year benefits not active this year
         */
        filteredUsedYtd(state) {
            const benefitsToSum = this.hasActiveFilters
                ? this.flattenedBenefits
                : state.benefits;

            const allPurelyCountBased = this.filteredAllPurelyCountBased;
            const { start: ytdStart, end: ytdEnd } =
                this.periodBoundaries.annual;

            return benefitsToSum.reduce((sum, item) => {
                const benefit = item.benefit;
                if (!benefit) return sum;

                if (allPurelyCountBased) {
                    return (
                        sum +
                        parseFloat(
                            item.count_used_current_period ||
                                item.current_period_usage ||
                                0
                        )
                    );
                }

                // Skip count-based and NCRED benefits to avoid
                // adding counts as dollar amounts in mixed scenarios
                if (this.checkIsCountBased(benefit)) {
                    return sum;
                }

                // Skip multi-year benefits not active this year (matches
                // _calculateTotalAnnualValueForBenefits exclusion logic)
                if (!this.isBenefitActiveThisYear(item, ytdStart, ytdEnd)) {
                    return sum;
                }

                return (
                    sum +
                    parseFloat(item.used_ytd ?? item.current_period_usage ?? 0)
                );
            }, 0);
        },

        /**
         * Calculate percentage used from filtered values
         */
        filteredPercentUsed() {
            const total = this.filteredTotalAnnualValue;
            const used = this.filteredUsedYtd;

            if (total === 0) return 0;

            return Math.round((used / total) * 100 * 10) / 10;
        },

        /**
         * Filtered benefit usage summary that respects active filters
         * Stats and table always show the same filtered data
         * Includes is_count_based flag for display formatting
         */
        filteredBenefitUsageSummary() {
            return {
                total_annual_value: this.filteredTotalAnnualValue,
                used_ytd: this.filteredUsedYtd,
                percent_used: this.filteredPercentUsed,
                is_count_based: this.filteredAllPurelyCountBased,
            };
        },

        /**
         * Get period boundaries for different period types
         * Returns start and end dates for quick comparison
         */
        periodBoundaries() {
            const now = dayjs();

            return {
                // Annual/Yearly
                annual: {
                    start: now.startOf('year').toDate(),
                    end: now.endOf('year').toDate(),
                },
                yearly: {
                    start: now.startOf('year').toDate(),
                    end: now.endOf('year').toDate(),
                },

                // Monthly
                monthly: {
                    start: now.startOf('month').toDate(),
                    end: now.endOf('month').toDate(),
                },

                // Quarterly
                quarterly: {
                    start: now.startOf('quarter').toDate(),
                    end: now.endOf('quarter').toDate(),
                },

                // Semiannual/Biannual (Jan-Jun or Jul-Dec)
                semiannual:
                    now.month() <= 5
                        ? {
                              start: now.startOf('year').toDate(),
                              end: now.month(5).endOf('month').toDate(),
                          }
                        : {
                              start: now.month(6).startOf('month').toDate(),
                              end: now.endOf('year').toDate(),
                          },
                biannual:
                    now.month() <= 5
                        ? {
                              start: now.startOf('year').toDate(),
                              end: now.month(5).endOf('month').toDate(),
                          }
                        : {
                              start: now.month(6).startOf('month').toDate(),
                              end: now.endOf('year').toDate(),
                          },
            };
        },

        cdnUrl() {
            return window.cdnUrl || '';
        },

        // ========================================================
        // Insights tab getters (MY1-2046) — derived from state.benefits
        // ========================================================
        /**
         * [start, end] dates derived from the active preset and shift count.
         * `start` is the first day of the first month in the window;
         * `end` is the last day of the last month in the window.
         */
        insightsDateRange(state) {
            const preset = state.insightsRangePreset;
            const shift = state.insightsRangeShiftCount;

            if (preset === 'this-year') {
                const reference = dayjs().subtract(shift, 'year');
                if (shift === 0) {
                    return [
                        reference.startOf('year').toDate(),
                        reference.endOf('month').toDate(),
                    ];
                }
                return [
                    reference.startOf('year').toDate(),
                    reference.endOf('year').toDate(),
                ];
            }

            if (preset === 'previous-year') {
                const reference = dayjs().subtract(1 + shift, 'year');
                return [
                    reference.startOf('year').toDate(),
                    reference.endOf('year').toDate(),
                ];
            }

            if (preset === 'this-month') {
                const reference = dayjs().subtract(shift, 'month');
                return [
                    reference.startOf('month').toDate(),
                    reference.endOf('month').toDate(),
                ];
            }

            if (preset === 'previous-month') {
                const reference = dayjs().subtract(1 + shift, 'month');
                return [
                    reference.startOf('month').toDate(),
                    reference.endOf('month').toDate(),
                ];
            }

            if (preset === 'custom' && state.insightsCustomRange) {
                const { startYear, startMonth, endYear, endMonth } =
                    state.insightsCustomRange;
                // Build dates from a fresh `new Date(year, month, 1)` rather
                // than chaining `.year().month()` on `dayjs()` — chaining
                // preserves today's day-of-month and overflows when the
                // target month has fewer days (e.g. Jan 31 → Feb 31 → Mar 3).
                const start = dayjs(new Date(startYear, startMonth, 1)).startOf(
                    'month'
                );
                const end = dayjs(new Date(endYear, endMonth, 1)).endOf(
                    'month'
                );
                const lengthMonths = end.diff(start, 'month') + 1;
                const offset = shift * lengthMonths;
                return [
                    start.subtract(offset, 'month').toDate(),
                    end.subtract(offset, 'month').toDate(),
                ];
            }

            // Fallback: current calendar year.
            const fallback = dayjs();
            return [
                fallback.startOf('year').toDate(),
                fallback.endOf('month').toDate(),
            ];
        },

        insightsStartDate() {
            return this.insightsDateRange[0];
        },

        insightsEndDate() {
            return this.insightsDateRange[1];
        },

        /**
         * Compact label for the current Insights date range — used by the
         * Period button and chart tooltips. Matches the Figma "Date format"
         * spec:
         *   • Single month        → "Apr 2026"
         *   • Whole year (Jan–Dec)→ "2025"
         *   • Same year, partial  → "Jan – Apr 2026"
         *   • Multi-year span     → "Jan 24 – Sep 2025"  (short year on start)
         */
        insightsDateRangeLabel() {
            const [start, end] = this.insightsDateRange;
            const s = dayjs(start);
            const e = dayjs(end);
            const sameMonth = s.year() === e.year() && s.month() === e.month();
            if (sameMonth) {
                return s.format('MMM YYYY');
            }
            if (s.year() === e.year() && s.month() === 0 && e.month() === 11) {
                return s.format('YYYY');
            }
            if (s.year() === e.year()) {
                return `${s.format('MMM')} – ${e.format('MMM YYYY')}`;
            }
            return `${s.format('MMM YY')} – ${e.format('MMM YYYY')}`;
        },

        hasActiveInsightsFilters(state) {
            return (
                state.insightsSelectedCards.length > 0 ||
                state.insightsSelectedBanks.length > 0 ||
                state.insightsSelectedPeriodTypes.length > 0 ||
                state.insightsSelectedUsage.length > 0 ||
                state.insightsSelectedCategories.length > 0 ||
                state.insightsSelectedUsers.length > 0
            );
        },

        /**
         * Benefits in scope for the Insights tab: applies Card / Bank / Period /
         * Usage / Category filter selections on top of the base `benefits` list.
         */
        insightsScopedBenefits(state) {
            const cards = state.insightsSelectedCards;
            const banks = state.insightsSelectedBanks;
            const periods = state.insightsSelectedPeriodTypes;
            const usage = state.insightsSelectedUsage;
            const categories = state.insightsSelectedCategories;
            const users = state.insightsSelectedUsers;
            const categoryMap = BENEFIT_CATEGORY_MAP;
            return state.benefits.filter((item) => {
                if (users.length > 0) {
                    const userId = item.user?.id;
                    const matched = users.some((u) => u.id === userId);
                    if (!matched) return false;
                }
                if (cards.length > 0) {
                    const matched = cards.some(
                        (c) =>
                            c.connected_account_id === item.connected_account_id
                    );
                    if (!matched) return false;
                }
                if (banks.length > 0) {
                    const matched = banks.some(
                        (b) =>
                            String(b.id) === String(item.card?.issuing_bank_id)
                    );
                    if (!matched) return false;
                }
                if (periods.length > 0) {
                    const benefitPeriod = this.normalizePeriodType(
                        item.benefit?.period_type
                    );
                    const matched = periods.some(
                        (p) => this.normalizePeriodType(p.id) === benefitPeriod
                    );
                    if (!matched) return false;
                }
                if (usage.length > 0) {
                    const used = this.insightsBenefitUsedInRange(item) > 0;
                    const matched = usage.some((u) =>
                        u.id === 'used' ? used : !used
                    );
                    if (!matched) return false;
                }
                if (categories.length > 0) {
                    const rawCategory = item.benefit?.category || 'other';
                    const groupId = categoryMap[rawCategory] || 'lifestyle';
                    const matched = categories.some((c) => c.id === groupId);
                    if (!matched) return false;
                }
                return true;
            });
        },

        /**
         * Number of monthly installments covered by the selected date range.
         * Used to prorate annual/semi-annual/quarterly benefits.
         */
        insightsMonthCount() {
            const [startDate, endDate] = this.insightsDateRange;
            const months = dayjs(endDate).diff(dayjs(startDate), 'month') + 1;
            return Math.max(1, months);
        },

        /**
         * Map a benefit's period_type to how many of those periods fit inside
         * the selected window. E.g. MONTHLY × 4-month window = 4 occurrences.
         */
        insightsPeriodOccurrences() {
            const months = this.insightsMonthCount;
            return {
                MONTHLY: months,
                QUARTERLY: Math.max(1, Math.ceil(months / 3)),
                SEMI_ANNUAL: Math.max(1, Math.ceil(months / 6)),
                SEMIANNUAL: Math.max(1, Math.ceil(months / 6)),
                BIANNUAL: Math.max(1, Math.ceil(months / 6)),
                // Scale annual occurrences with the window like the other
                // period types — a 24-month custom range over an annual
                // benefit should count as 2 occurrences, not 1.
                ANNUAL: Math.max(1, Math.ceil(months / 12)),
                YEARLY: Math.max(1, Math.ceil(months / 12)),
                ONE_TIME: 1,
            };
        },

        /**
         * Total value accrued per benefit within the selected date range
         * (amount × count × occurrences). Count-based benefits without a
         * monetary amount (e.g. companion passes) have no dollar value
         * and contribute 0. Count-based benefits with a per-unit amount
         * (e.g. 20 × $8 internet credits) multiply amount × count.
         */
        insightsBenefitValueInRange() {
            const occurrences = this.insightsPeriodOccurrences;
            return (item) => {
                const isCountBased =
                    item?.benefit?.values_summary?.is_count_based === true;
                const amount = parseFloat(item?.benefit?.amount || 0);
                if (isCountBased && !(amount > 0)) {
                    return 0;
                }
                const count = isCountBased
                    ? parseFloat(item?.benefit?.value_details?.count) || 1
                    : 1;
                const periodType = this.normalizePeriodType(
                    item?.benefit?.period_type
                );
                const multiplier = occurrences[periodType] ?? 1;
                return amount * count * multiplier;
            };
        },

        /**
         * Per-item used-in-range totals keyed by benefit-item reference.
         * Precomputed once per (`state.benefits`, `insightsDateRange`) change
         * so the derived getters (`insightsScopedBenefits`, `insightsSummary`,
         * `redeemedByCard`, `insightsBenefits`, etc.) don't re-walk every
         * benefit's usage_records on each access.
         */
        insightsUsedByItem(state) {
            const [startDate, endDate] = this.insightsDateRange;
            const start = dayjs(startDate).startOf('day');
            const end = dayjs(endDate).endOf('day');
            const map = new Map();
            for (const item of state.benefits) {
                const isCountBased =
                    item?.benefit?.values_summary?.is_count_based === true;
                const amount = parseFloat(item?.benefit?.amount || 0);
                if (isCountBased && !(amount > 0)) {
                    map.set(item, 0);
                    continue;
                }
                const records = item?.usage_records || [];
                let sum = 0;
                for (const r of records) {
                    const when = dayjs(r.used_at);
                    if (when.isSameOrAfter(start) && when.isSameOrBefore(end)) {
                        sum += parseFloat(r.total_amount_used || 0);
                    }
                }
                map.set(item, sum);
            }
            return map;
        },

        /**
         * Sum of usage for a benefit whose `used_at` falls in the selected range.
         * Count-based benefits without a monetary amount can't be expressed in
         * dollars (`total_amount_used` stores the count, not currency) so they
         * are excluded from $ aggregates.
         *
         * Reads from `insightsUsedByItem` (Map, O(1) lookup) for items belonging
         * to `state.benefits`. Falls back to on-demand computation for stray
         * items not in the precomputed map (e.g. ad-hoc test inputs).
         */
        insightsBenefitUsedInRange() {
            const map = this.insightsUsedByItem;
            const [startDate, endDate] = this.insightsDateRange;
            return (item) => {
                if (map.has(item)) {
                    return map.get(item);
                }
                const isCountBased =
                    item?.benefit?.values_summary?.is_count_based === true;
                const amount = parseFloat(item?.benefit?.amount || 0);
                if (isCountBased && !(amount > 0)) {
                    return 0;
                }
                const records = item?.usage_records || [];
                const start = dayjs(startDate).startOf('day');
                const end = dayjs(endDate).endOf('day');
                return records.reduce((sum, r) => {
                    const when = dayjs(r.used_at);
                    if (when.isSameOrAfter(start) && when.isSameOrBefore(end)) {
                        return sum + parseFloat(r.total_amount_used || 0);
                    }
                    return sum;
                }, 0);
            };
        },

        insightsSummary() {
            const benefits = this.insightsScopedBenefits;
            let total = 0;
            let used = 0;
            benefits.forEach((item) => {
                total += this.insightsBenefitValueInRange(item);
                used += this.insightsBenefitUsedInRange(item);
            });
            const unused = Math.max(total - used, 0);
            const utilization =
                total > 0 ? Math.round((used / total) * 1000) / 10 : 0;
            return {
                total_value: total,
                used,
                unused,
                utilization,
            };
        },

        /**
         * Monthly `used` + `unused` series for the selected range.
         *
         * `used`   — sum of every usage record that fell in the month,
         *            regardless of benefit period type.
         * `unused` — the portion of *monthly* benefits that was available
         *            that month but not redeemed. Non-monthly benefits
         *            (annual, quarterly, semi-annual, one-time) never
         *            contribute to `unused`, otherwise those large annual
         *            values drown out the monthly signal and every bar
         *            ends up the same height. Future months are excluded
         *            because the accrual hasn't happened yet.
         */
        benefitsUsageSeries() {
            // Return enticing placeholder data for non-subscribed users
            if (!this.isSubscribed) {
                const [startDate, endDate] = this.insightsDateRange;
                const start = dayjs(startDate).startOf('month');
                const end = dayjs(endDate).endOf('month');
                const months = [];

                let cursor = start;
                let monthIndex = 0;
                while (cursor.isSameOrBefore(end)) {
                    // Create realistic-looking usage pattern
                    const baseUsed = 150 + Math.sin(monthIndex * 0.5) * 50;
                    const baseUnused = 200 + Math.cos(monthIndex * 0.3) * 30;

                    months.push({
                        key: cursor.format('YYYY-MM'),
                        label: cursor.format('MMM YYYY'),
                        used: Math.max(0, Math.round(baseUsed * 100) / 100),
                        unused: Math.max(0, Math.round(baseUnused * 100) / 100),
                        usedByPeriod: {
                            Monthly: Math.max(
                                0,
                                Math.round(baseUsed * 0.6 * 100) / 100
                            ),
                            Quarterly: Math.max(
                                0,
                                Math.round(baseUsed * 0.25 * 100) / 100
                            ),
                            Annually: Math.max(
                                0,
                                Math.round(baseUsed * 0.15 * 100) / 100
                            ),
                        },
                    });
                    cursor = cursor.add(1, 'month');
                    monthIndex++;
                }
                return months;
            }

            const benefits = this.insightsScopedBenefits;
            const [startDate, endDate] = this.insightsDateRange;
            const start = dayjs(startDate).startOf('month');
            const end = dayjs(endDate).endOf('month');
            const months = [];

            let cursor = start;
            while (cursor.isSameOrBefore(end)) {
                months.push({
                    key: cursor.format('YYYY-MM'),
                    label: cursor.format('MMM YYYY'),
                    monthStart: cursor.startOf('month'),
                    monthEnd: cursor.endOf('month'),
                });
                cursor = cursor.add(1, 'month');
            }

            const currentMonthKey = dayjs().format('YYYY-MM');

            const periodLabel = (periodType) => {
                switch (this.normalizePeriodType(periodType)) {
                    case 'MONTHLY':
                        return 'Monthly';
                    case 'QUARTERLY':
                        return 'Quarterly';
                    case 'SEMI_ANNUAL':
                    case 'SEMIANNUAL':
                    case 'BIANNUAL':
                        return 'Semi-annually';
                    case 'ANNUAL':
                    case 'YEARLY':
                        return 'Annually';
                    case 'ONE_TIME':
                        return 'One-time';
                    default:
                        return 'Other';
                }
            };

            return months.map((m) => {
                let used = 0;
                let unused = 0;
                const usedByPeriod = {};
                const isFuture = m.key > currentMonthKey;

                benefits.forEach((item) => {
                    const periodType = this.normalizePeriodType(
                        item.benefit?.period_type
                    );
                    const isCountBased =
                        item.benefit?.values_summary?.is_count_based === true;
                    const amount = parseFloat(item.benefit?.amount || 0);
                    // Count-based benefits without a $ amount can't be
                    // expressed in dollars and shouldn't add to either bar.
                    if (isCountBased && !(amount > 0)) {
                        return;
                    }
                    const count = isCountBased
                        ? parseFloat(item.benefit?.value_details?.count) || 1
                        : 1;
                    // Only MONTHLY benefits contribute to the per-month
                    // unused signal — non-monthly benefits don't accrue
                    // on a monthly cadence so prorating them across the
                    // bar chart misrepresents what was actually available
                    // each month.
                    const monthlyAccrual =
                        periodType === 'MONTHLY' ? amount * count : 0;
                    const label = periodLabel(item.benefit?.period_type);

                    let usedThisMonth = 0;
                    (item.usage_records || []).forEach((r) => {
                        const when = dayjs(r.used_at);
                        if (
                            when.isSameOrAfter(m.monthStart) &&
                            when.isSameOrBefore(m.monthEnd)
                        ) {
                            usedThisMonth += parseFloat(
                                r.total_amount_used || 0
                            );
                        }
                    });

                    used += usedThisMonth;
                    if (usedThisMonth > 0) {
                        usedByPeriod[label] =
                            (usedByPeriod[label] || 0) + usedThisMonth;
                    }

                    if (!isFuture && monthlyAccrual > 0) {
                        unused += Math.max(monthlyAccrual - usedThisMonth, 0);
                    }
                });

                Object.keys(usedByPeriod).forEach((k) => {
                    usedByPeriod[k] = Math.round(usedByPeriod[k] * 100) / 100;
                });

                return {
                    label: m.label,
                    used: Math.round(used * 100) / 100,
                    unused: Math.round(unused * 100) / 100,
                    usedByPeriod,
                };
            });
        },

        /**
         * Amount redeemed per card within the selected range. Empty cards
         * drop out. Sorted descending for the donut chart.
         */
        redeemedByCard() {
            // Return enticing placeholder data for non-subscribed users
            if (!this.isSubscribed) {
                return [
                    {
                        name: 'Card 1',
                        last_4: '1234',
                        user_name: 'John',
                        amount: 2450,
                    },
                    {
                        name: 'Card 2',
                        last_4: '5678',
                        user_name: 'John',
                        amount: 1890,
                    },
                    {
                        name: 'Card 3',
                        last_4: '9012',
                        user_name: 'Jane',
                        amount: 1340,
                    },
                    {
                        name: 'Card 4',
                        last_4: '3456',
                        user_name: 'John',
                        amount: 980,
                    },
                ];
            }

            const byCard = new Map();
            this.insightsScopedBenefits.forEach((item) => {
                const used = this.insightsBenefitUsedInRange(item);
                if (used <= 0) return;
                const key = item.connected_account_id;
                const existing = byCard.get(key) || {
                    name: item.card?.title || 'Unknown card',
                    last_4: item.connected_account_last_four || '',
                    user_name: item.user?.name || '',
                    amount: 0,
                };
                existing.amount += used;
                byCard.set(key, existing);
            });
            const all = Array.from(byCard.values()).sort(
                (a, b) => b.amount - a.amount
            );
            // Figma: 5 cards (max) shown individually; from 6+ aggregate
            // the remainder into a single "Other N cards" entry. Keep the
            // individual cards on the entry so the tooltip can break it down.
            if (all.length <= 5) return all;
            const top = all.slice(0, 5);
            const others = all.slice(5);
            const otherTotal = others.reduce(
                (sum, c) => sum + Number(c.amount || 0),
                0
            );
            return [
                ...top,
                {
                    name: `Other ${others.length} cards`,
                    last_4: '',
                    user_name: '',
                    amount: otherTotal,
                    isOther: true,
                    cards: others,
                },
            ];
        },

        /**
         * Aggregate usage per card with total accrued value for the range, so
         * the UI can render the percent bar + "$used of $total" label.
         */
        usageByCard() {
            // Return enticing placeholder data for non-subscribed users
            if (!this.isSubscribed) {
                return [
                    {
                        name: 'Card 1',
                        last_4: '1234',
                        user_name: 'John',
                        image: null,
                        used: 2450,
                        total: 3000,
                        percent: 81.7,
                    },
                    {
                        name: 'Card 2',
                        last_4: '5678',
                        user_name: 'John',
                        image: null,
                        used: 1890,
                        total: 2400,
                        percent: 78.8,
                    },
                    {
                        name: 'Card 3',
                        last_4: '9012',
                        user_name: 'Jane',
                        image: null,
                        used: 1340,
                        total: 1800,
                        percent: 74.4,
                    },
                    {
                        name: 'Card 4',
                        last_4: '3456',
                        user_name: 'John',
                        image: null,
                        used: 980,
                        total: 1500,
                        percent: 65.3,
                    },
                ];
            }

            const cdn = this.cdnUrl;
            const resolveImage = (image) => {
                if (image?.path && image?.file_name) {
                    return `${cdn}/${image.path}/_small/${image.file_name}`;
                }
                return null;
            };

            const byCard = new Map();
            this.insightsScopedBenefits.forEach((item) => {
                const total = this.insightsBenefitValueInRange(item);
                const used = this.insightsBenefitUsedInRange(item);
                const key = item.connected_account_id;
                const existing = byCard.get(key) || {
                    name: item.card?.title || 'Unknown card',
                    last_4: item.connected_account_last_four || '',
                    user_name: item.user?.name || '',
                    image: resolveImage(item.card?.image),
                    used: 0,
                    total: 0,
                };
                existing.used += used;
                existing.total += total;
                byCard.set(key, existing);
            });
            return Array.from(byCard.values())
                .filter((c) => c.total > 0 && c.used > 0)
                .map((c) => ({
                    ...c,
                    percent:
                        c.total > 0
                            ? Math.round((c.used / c.total) * 1000) / 10
                            : 0,
                }))
                .sort((a, b) => b.percent - a.percent);
        },

        /**
         * Flat list of benefits formatted for the Insights historical table.
         */
        insightsBenefits() {
            const frequencyLabel = (periodType) => {
                switch (this.normalizePeriodType(periodType)) {
                    case 'MONTHLY':
                        return 'Monthly';
                    case 'QUARTERLY':
                        return 'Quarterly';
                    case 'SEMI_ANNUAL':
                    case 'SEMIANNUAL':
                    case 'BIANNUAL':
                        return 'Semi-annual';
                    case 'ANNUAL':
                    case 'YEARLY':
                        return 'Annual';
                    case 'ONE_TIME':
                        return 'One-time';
                    default:
                        return 'Other';
                }
            };

            const mapped = this.insightsScopedBenefits.map((item) => {
                const total = this.insightsBenefitValueInRange(item);
                const used = this.insightsBenefitUsedInRange(item);
                const unused = Math.max(total - used, 0);
                const utilization =
                    total > 0 ? Math.round((used / total) * 1000) / 10 : 0;
                const amount = parseFloat(item.benefit?.amount || 0);
                const frequency = frequencyLabel(item.benefit?.period_type);
                const cadenceLabel = {
                    Monthly: '/month',
                    Annual: '/year',
                    Quarterly: '/quarter',
                    'Semi-annual': '/6 months',
                    'One-time': '',
                    Other: '',
                }[frequency];
                // Annualized $ value used by the card-group header pill so the
                // "Up to $X/year" badge always shows yearly value regardless
                // of the selected period.
                const annualMultiplier =
                    {
                        Monthly: 12,
                        Quarterly: 4,
                        'Semi-annual': 2,
                        Annual: 1,
                        'One-time': 1,
                    }[frequency] || 1;
                const count =
                    parseFloat(item.benefit?.value_details?.count) || 1;
                const annualValue = amount * annualMultiplier * count;
                const expirationTime = this.getExpirationTime(
                    item.period_end_date,
                    item.benefit?.period_type
                );
                return {
                    item,
                    id: item.benefit?.id,
                    connected_account_id: item.connected_account_id,
                    name: item.benefit?.benefit_title,
                    card_name: item.card?.title,
                    card_last_4: item.connected_account_last_four,
                    card_id: item.card?.id,
                    card_image: item.card?.image,
                    value_up_to:
                        amount > 0
                            ? `$${amount.toLocaleString('en-US')}${cadenceLabel || ''}`
                            : '—',
                    used,
                    total,
                    unused,
                    utilization,
                    frequency,
                    annual_value: annualValue,
                    expiration_time: expirationTime,
                };
            });
            return this.applyInsightsSort(mapped);
        },

        /**
         * Insights benefits grouped by card when `insightsGroupBy === 'by_card'`.
         * Returns `{ mode: 'flat' | 'by_card', rows, groups }`. Each group
         * carries aggregate `total/used/unused/utilization` summed across its
         * rows so the card header can render the same stats as a flat row.
         */
        insightsBenefitsGrouped() {
            const rows = this.insightsBenefits;
            if (this.insightsGroupBy !== 'by_card') {
                return { mode: 'flat', rows, groups: [] };
            }
            const groupsMap = new Map();
            rows.forEach((row) => {
                const key = `${row.card_id || 'unknown'}-${row.connected_account_id || ''}`;
                if (!groupsMap.has(key)) {
                    groupsMap.set(key, {
                        key,
                        card_name: row.card_name || 'Unknown Card',
                        card_last_4: row.card_last_4,
                        card_image: row.card_image,
                        rows: [],
                        total: 0,
                        used: 0,
                        annual_total: 0,
                    });
                }
                const group = groupsMap.get(key);
                group.rows.push(row);
                group.total += Number(row.total) || 0;
                group.used += Number(row.used) || 0;
                group.annual_total += Number(row.annual_value) || 0;
            });
            const groups = Array.from(groupsMap.values()).map((g) => {
                const unused = Math.max(g.total - g.used, 0);
                const utilization =
                    g.total > 0
                        ? Math.round((g.used / g.total) * 1000) / 10
                        : 0;
                return { ...g, unused, utilization };
            });
            return { mode: 'by_card', rows, groups };
        },

        /**
         * Cards available for the Insights Card filter, grouped by user
         * (mirrors `benefits-card-dropdown.vue` shape).
         */
        insightsUsersWithCards(state) {
            const users = new Map();
            (state.freemium.walletUsers || []).forEach((user) => {
                users.set(user.id, { id: user.id, name: user.name, cards: [] });
            });
            state.benefits.forEach((item) => {
                const userId = item.user?.id;
                if (!userId) return;
                if (!users.has(userId)) {
                    users.set(userId, {
                        id: userId,
                        name: item.user?.name || 'Unknown',
                        cards: [],
                    });
                }
                const user = users.get(userId);
                const exists = user.cards.find(
                    (c) => c.connected_account_id === item.connected_account_id
                );
                if (!exists) {
                    user.cards.push({
                        connected_account_id: item.connected_account_id,
                        title: item.card?.title || 'Unknown card',
                        last_four: item.connected_account_last_four || '',
                        card_image: item.card?.image,
                    });
                }
            });
            return Array.from(users.values()).filter((u) => u.cards.length > 0);
        },

        /**
         * User options for the Insights User filter. Derived from the
         * benefits dataset so it only includes wallet members that actually
         * have at least one tracked benefit.
         */
        insightsUserOptions(state) {
            const users = new Map();
            state.benefits.forEach((item) => {
                const id = item.user?.id;
                if (id == null || users.has(id)) return;
                users.set(id, {
                    id,
                    name: item.user?.name || 'Unknown',
                    is_business: !!item.user?.is_business,
                });
            });
            return Array.from(users.values()).sort((a, b) =>
                a.name.localeCompare(b.name)
            );
        },

        /**
         * Bank options for the Insights Bank filter, derived from benefits.
         */
        insightsBankOptions(state) {
            const banks = new Map();
            state.benefits.forEach((item) => {
                const bankId = item.card?.issuing_bank_id;
                const bankName = item.card?.issuing_bank_name;
                if (!bankId || banks.has(bankId)) return;
                banks.set(bankId, {
                    id: bankId,
                    name: bankName || `Bank ${bankId}`,
                    logo_url: item.card?.issuing_bank_logo_url || null,
                });
            });
            return Array.from(banks.values()).sort((a, b) =>
                a.name.localeCompare(b.name)
            );
        },

        /**
         * Static usage options for the Insights Usage filter.
         */
        insightsUsageOptions() {
            return [
                { id: 'used', name: 'Used' },
                { id: 'unused', name: 'Unused' },
            ];
        },

        /**
         * Category options derived from the current benefits dataset: maps
         * each raw benefit category to its display group, dedupes, and sorts
         * alphabetically. Shared by both the Current and Insights tab
         * Category filters.
         */
        insightsCategoryOptions(state) {
            const groupIds = new Set();
            state.benefits.forEach((item) => {
                const raw = item.benefit?.category || 'other';
                groupIds.add(BENEFIT_CATEGORY_MAP[raw] || 'lifestyle');
            });
            return Array.from(groupIds)
                .map((id) => ({ id, name: BENEFIT_CATEGORY_LABELS[id] || id }))
                .sort((a, b) => a.name.localeCompare(b.name));
        },

        /**
         * Alias for {@link insightsCategoryOptions} — kept so the Current tab
         * filter UI can reference its tab-appropriate name.
         */
        availableCategories() {
            return this.insightsCategoryOptions;
        },

        /**
         * Period type options for the Insights Period filter.
         */
        insightsPeriodOptions(state) {
            const displayNames = {
                monthly: 'Monthly',
                quarterly: 'Quarterly',
                semi_annual: 'Semi-annual',
                semiannual: 'Semi-annual',
                annual: 'Annual',
                biennial: 'Every 2 years',
                triennial: 'Every 3 years',
                quadrennial: 'Every 4 years',
                quinquennial: 'Every 5 years',
                decennial: 'Every 10 years',
                statement: 'Per statement',
            };
            // Sort shortest → longest cycle (Figma spec).
            const periodMonths = {
                statement: 1,
                monthly: 1,
                quarterly: 3,
                semi_annual: 6,
                semiannual: 6,
                annual: 12,
                biennial: 24,
                triennial: 36,
                quadrennial: 48,
                quinquennial: 60,
                decennial: 120,
            };
            const periods = new Map();
            state.benefits.forEach((item) => {
                const periodType = item.benefit?.period_type?.toLowerCase();
                if (!periodType || periods.has(periodType)) return;
                periods.set(periodType, {
                    id: periodType,
                    name: displayNames[periodType] || periodType,
                });
            });
            return Array.from(periods.values()).sort(
                (a, b) =>
                    (periodMonths[a.id] ?? Infinity) -
                    (periodMonths[b.id] ?? Infinity)
            );
        },
    },

    actions: {
        // Normalize period type to handle variations (SEMI_ANNUAL, semiannual, semi-annual, etc.)
        normalizePeriodType(periodType) {
            if (!periodType) return null;
            return periodType.toUpperCase().replace(/-/g, '_');
        },

        getPeriodKey(dateInput, periodType) {
            // Use shared utility to parse date as local to avoid timezone shifts
            const localDate = parseLocalDate(dateInput);
            if (!localDate) return null;
            const d = dayjs(localDate);
            if (!d.isValid()) return null;

            const type = String(periodType || 'annual')
                .toLowerCase()
                .replace(/[_-]/g, '');

            if (type === 'monthly') {
                return d.format('YYYY-MM');
            }
            if (type === 'quarterly') {
                const q = Math.floor(d.month() / 3) + 1;
                return `${d.year()}-Q${q}`;
            }
            if (type === 'semiannual' || type === 'biannual') {
                const half = d.month() <= 5 ? 'H1' : 'H2';
                return `${d.year()}-${half}`;
            }
            if (type === 'daily') {
                return d.format('YYYY-MM-DD');
            }
            return String(d.year());
        },

        formatYear(dateInput) {
            const d = dayjs(dateInput);
            if (!d.isValid()) return '';

            const currentYear = dayjs().year();
            const year = d.year();

            // If it's the current year, show month and year for more context
            if (year === currentYear) {
                return d.format('MMM YYYY');
            }

            return String(year);
        },

        checkIsCountBased(benefit) {
            const isCountBased = benefit?.values_summary?.is_count_based;
            const currency = benefit?.values_summary?.currency;
            if (currency === 'NCRED') return true;
            return isCountBased && parseFloat(benefit?.amount || 0) === 0;
        },

        // Calculate expiration time display (shared across components)
        getExpirationTime(periodEndDate, periodType) {
            if (!periodEndDate) {
                return null;
            }

            const endDate = dayjs(periodEndDate).endOf('day');
            const now = dayjs();

            // If the date has passed, don't show anything
            if (endDate.isBefore(now)) {
                return null;
            }

            const diffInDays = endDate.diff(now, 'day');
            const diffInMonths = endDate.diff(now, 'month');
            const normalizedPeriodType = this.normalizePeriodType(periodType);

            // For monthly benefits, always show in days
            if (normalizedPeriodType === 'MONTHLY') {
                if (diffInDays === 0) return 'Today';
                if (diffInDays === 1) return '1 day';
                return `${diffInDays} days`;
            }

            // For annual/semiannual benefits
            if (
                normalizedPeriodType === 'ANNUAL' ||
                normalizedPeriodType === 'SEMI_ANNUAL'
            ) {
                // If more than 1 month away, show in months
                if (diffInMonths > 1) {
                    return `${diffInMonths} months`;
                }

                // If 1 month or less, show in days
                if (diffInDays === 0) return 'Today';
                if (diffInDays === 1) return '1 day';
                return `${diffInDays} days`;
            }

            // For quarterly or other types, show in days if less than a month, otherwise months
            if (diffInMonths >= 1) {
                return diffInMonths === 1
                    ? '1 month'
                    : `${diffInMonths} months`;
            }

            if (diffInDays === 0) return 'Today';
            if (diffInDays === 1) return '1 day';
            return `${diffInDays} days`;
        },

        // Get period type in different formats
        getPeriodTypeFormat(periodType, format = 'adjective') {
            if (!periodType) {
                return format === 'noun' ? 'year' : 'annual';
            }

            const formats = {
                monthly: {
                    noun: 'month',
                    adjective: 'monthly',
                },
                quarterly: {
                    noun: 'quarter',
                    adjective: 'quarterly',
                },
                semi_annual: {
                    noun: '6 months',
                    adjective: 'semi-annual',
                },
                semiannual: {
                    noun: '6 months',
                    adjective: 'semi-annual',
                },
                annual: {
                    noun: 'year',
                    adjective: 'annual',
                },
                biennial: {
                    noun: '2 years',
                    adjective: 'every 2 years',
                },
                triennial: {
                    noun: '3 years',
                    adjective: 'every 3 years',
                },
                quadrennial: {
                    noun: '4 years',
                    adjective: 'every 4 years',
                },
                quinquennial: {
                    noun: '5 years',
                    adjective: 'every 5 years',
                },
                decennial: {
                    noun: '10 years',
                    adjective: 'every 10 years',
                },
                statement: {
                    noun: 'statement',
                    adjective: 'per statement',
                },
            };

            const periodData = formats[periodType.toLowerCase()];
            if (periodData) {
                return periodData[format] || periodData.adjective;
            }

            return periodType.toLowerCase();
        },

        // Backwards compatibility - returns adjective form
        formatPeriodType(periodType) {
            return this.getPeriodTypeFormat(periodType, 'adjective');
        },

        // Store the unified response data
        setBenefitsData(data) {
            this.benefits = data.flat.benefits || [];
            this.totalCount = data.flat.total_count || 0;
            this.benefitsByCard = data.by_card || [];

            this.benefitUsageSummary = data.benefit_usage_summary || null;

            // Set freemium data
            if (data.freemium) {
                this.freemium = {
                    isSubscribed: data.freemium.is_subscribed || false,
                    trackedConnectedAccountIds:
                        data.freemium.tracked_connected_account_ids || [],
                    maxTrackedCards: data.freemium.max_tracked_cards || 1,
                    availableCards: data.freemium.available_cards || [],
                    walletUsersCount: data.freemium.wallet_users_count || 1,
                    walletUsers: data.freemium.wallet_users || [],
                    bannerDismissed: data.freemium.banner_dismissed || false,
                    monthlySwitchesUsed:
                        data.freemium.monthly_switches_used || 0,
                    maxMonthlySwitches: data.freemium.max_monthly_switches || 3,
                };
                this.freemiumLoaded = true;
            }

            if (data.display_preferences) {
                if (
                    data.display_preferences.show_hidden_benefits !== undefined
                ) {
                    this.showHiddenBenefits =
                        data.display_preferences.show_hidden_benefits;
                }
            }

            this.error = null;
            this.hasError = false;
        },

        // Set error state
        setError(error) {
            this.error = error;
            this.hasError = true;
            this.benefits = [];
            this.totalCount = 0;
            this.benefitsByCard = null;

            this.benefitUsageSummary = null;
        },

        // Clear error state
        clearError() {
            this.error = null;
            this.hasError = false;
        },

        // Helper function for sorting
        getSortValue(item, column) {
            switch (column) {
                case 'benefit':
                    return item.benefit_title || '';
                case 'value':
                    return item.amount || 0;
                case 'usage':
                    return item.usage?.used_current_period || 0;
                case 'expires_in':
                    return item.expires_in || '';
                default:
                    return '';
            }
        },

        extractNumericValue(text) {
            if (!text) return 0;
            const match = text.match(/\$?(\d+(?:,\d{3})*(?:\.\d{2})?)/);
            return match ? parseFloat(match[1].replace(/,/g, '')) : 0;
        },

        applyFilters(array) {
            let filtered = array;

            if (this.selectedCards.length > 0) {
                filtered = filtered.filter((item) =>
                    this.selectedCards.some(
                        (card) =>
                            card.connected_account_id ===
                            item.connected_account_id
                    )
                );
            }

            if (this.selectedUsageTypes.length > 0) {
                filtered = filtered.filter((item) => {
                    const hasUsageRecords = item.usage_records?.length > 0;

                    return this.selectedUsageTypes.some((usageType) => {
                        if (usageType.id === 'redeemed') {
                            return hasUsageRecords;
                        } else if (usageType.id === 'not_redeemed') {
                            return !hasUsageRecords;
                        }
                        return false;
                    });
                });
            }

            if (this.selectedUsers.length > 0) {
                filtered = filtered.filter((item) =>
                    this.selectedUsers.some((user) => user.id === item.user?.id)
                );
            }

            if (this.selectedBanks.length > 0) {
                filtered = filtered.filter((item) =>
                    this.selectedBanks.some(
                        (bank) => bank.id === item.card?.issuing_bank_id
                    )
                );
            }

            if (this.selectedPeriodTypes.length > 0) {
                filtered = filtered.filter((item) =>
                    this.selectedPeriodTypes.some(
                        (periodType) =>
                            periodType.id?.toLowerCase() ===
                            item.benefit?.period_type?.toLowerCase()
                    )
                );
            }

            if (this.selectedCategories.length > 0) {
                filtered = filtered.filter((item) => {
                    const raw = item.benefit?.category || 'other';
                    const groupId = BENEFIT_CATEGORY_MAP[raw] || 'lifestyle';
                    return this.selectedCategories.some(
                        (c) => c.id === groupId
                    );
                });
            }

            return filtered;
        },

        applySorting(array) {
            if (!this.sortConfig.column) return array;

            return [...array].sort((a, b) => {
                // Freemium: unlocked benefits always sort before locked ones
                if (a.isLocked !== b.isLocked) {
                    return a.isLocked ? 1 : -1;
                }

                const { column, direction } = this.sortConfig;
                let aValue, bValue;

                switch (column) {
                    case 'benefit':
                        aValue = a.benefit?.benefit_title || '';
                        bValue = b.benefit?.benefit_title || '';
                        break;
                    case 'card':
                        aValue = a.card?.title || '';
                        bValue = b.card?.title || '';
                        break;
                    case 'value_up_to':
                        aValue = parseFloat(a.benefit?.amount || 0);
                        bValue = parseFloat(b.benefit?.amount || 0);
                        break;
                    case 'used_current_period':
                        aValue = parseFloat(a.current_period_usage || 0);
                        bValue = parseFloat(b.current_period_usage || 0);
                        break;
                    case 'user':
                        aValue = a.user?.name || '';
                        bValue = b.user?.name || '';
                        break;
                    case 'expires_in':
                        aValue = a.period_end_date || '';
                        bValue = b.period_end_date || '';
                        break;
                    default:
                        return 0;
                }

                // Handle string vs number comparison
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    const comparison = aValue.localeCompare(bValue);
                    return direction === 'asc' ? comparison : -comparison;
                } else {
                    const comparison = aValue - bValue;
                    return direction === 'asc' ? comparison : -comparison;
                }
            });
        },

        /**
         * Re-fetch benefits without toggling the loading spinner so the
         * Insights aggregates re-derive after a usage save/edit/delete.
         * Used when the optimistic state has already been applied and we
         * just need to sync against the authoritative backend totals.
         */
        // Resolves true when the benefits were replaced (and the open
        // sidebar's selectedBenefit re-pointed), false when the request failed.
        async refreshBenefitsSilently() {
            const targetWalletId = this.walletId;
            if (!targetWalletId) return false;
            try {
                const response = await ApiService.apiCall(
                    `/wallets/${targetWalletId}/card-benefits`,
                    'GET'
                );
                this.setBenefitsData(response.data);

                // setBenefitsData replaced the benefits array, so the
                // sidebar's selectedBenefit ref now points to a stale
                // object. Re-point it at the corresponding fresh entry
                // so the sidebar reflects the canonical state too.
                if (this.selectedBenefit) {
                    const fresh = this.benefits.find(
                        (b) =>
                            b?.benefit?.card_benefit_id ===
                                this.selectedBenefit?.benefit
                                    ?.card_benefit_id &&
                            b?.connected_account_id ===
                                this.selectedBenefit?.connected_account_id
                    );
                    if (fresh) {
                        this.selectedBenefit = fresh;
                    }
                }

                return true;
            } catch (err) {
                console.error('Failed to silently refresh card benefits:', err);

                return false;
            }
        },

        async fetchBenefits(walletId = null) {
            this.loading = true;
            this.clearError();

            // Tag this invocation so the deferred clear in `finally` only
            // fires for the latest call. Without this guard, two back-to-back
            // calls race: call 1's 500ms timeout would clear `loading` while
            // call 2 is still in-flight (Devin review BUG_…_0001).
            const fetchId = this.fetchId + 1;
            this.fetchId = fetchId;
            const startedAt = Date.now();

            try {
                const targetWalletId = walletId || this.walletId;

                if (!targetWalletId) {
                    throw new Error('Wallet ID is required');
                }

                const response = await ApiService.apiCall(
                    `/wallets/${targetWalletId}/card-benefits`,
                    'GET'
                );

                this.setBenefitsData(response.data);
            } catch (err) {
                console.error('Failed to fetch card benefits:', err);
                this.setError(err);

                // Error notification
                notify({
                    group: 'error',
                    title: 'Failed to Load Benefits',
                    text: 'Unable to load your card benefits. Please try again.',
                    type: 'error',
                });
            } finally {
                // Preserve the original 500ms minimum-loading UX while only
                // delaying for the remaining time, and only clear `loading`
                // if no newer fetch has started in the meantime.
                const remaining = Math.max(0, 500 - (Date.now() - startedAt));
                setTimeout(() => {
                    if (this.fetchId === fetchId) {
                        this.loading = false;
                    }
                }, remaining);
            }
        },

        async setViewMode(mode, walletId = null) {
            if (mode !== this.viewMode) {
                this.viewMode = mode;
                // await this.fetchBenefits(walletId);
            }
        },

        // Save tracked cards selection
        async saveTrackedCards(connectedAccountIds) {
            try {
                const targetWalletId = this.walletId;

                if (!targetWalletId) {
                    throw new Error('Wallet ID is required');
                }

                const response = await ApiService.apiCall(
                    `/wallets/${targetWalletId}/card-benefits/tracked-cards`,
                    'POST',
                    { connected_account_ids: connectedAccountIds }
                );

                if (response.data.success) {
                    this.freemium.trackedConnectedAccountIds =
                        response.data.tracked_connected_account_ids;

                    // Close modal first before refreshing data
                    this.showSelectCardsModal = false;

                    // Refresh benefits data to reflect new tracked cards
                    await this.fetchBenefits();

                    return { success: true };
                }

                return { success: false, error: response.data.error };
            } catch (err) {
                console.error('Failed to save tracked cards:', err);

                // Check if switch limit reached
                if (
                    err.response?.status === 403 &&
                    err.response?.data?.switch_limit_reached
                ) {
                    // Update local state with server values
                    this.freemium.monthlySwitchesUsed =
                        err.response.data.monthly_switches_used;
                    this.freemium.maxMonthlySwitches =
                        err.response.data.max_monthly_switches;

                    return {
                        success: false,
                        switchLimitReached: true,
                        error: err.response.data.error,
                        monthlySwitchesUsed:
                            err.response.data.monthly_switches_used,
                        maxMonthlySwitches:
                            err.response.data.max_monthly_switches,
                    };
                }

                // Check if upgrade is required
                if (
                    err.response?.status === 403 &&
                    err.response?.data?.requires_upgrade
                ) {
                    return {
                        success: false,
                        requiresUpgrade: true,
                        maxAllowed: err.response.data.max_allowed,
                    };
                }

                notify({
                    group: 'error',
                    title: 'Failed to Update',
                    text: 'Unable to update tracked cards. Please try again.',
                    type: 'error',
                });

                return { success: false, error: err.message };
            }
        },

        // Dismiss freemium banner
        async dismissBanner() {
            try {
                const targetWalletId = this.walletId;

                if (!targetWalletId) {
                    throw new Error('Wallet ID is required');
                }

                const response = await ApiService.apiCall(
                    `/wallets/${targetWalletId}/card-benefits/dismiss-banner`,
                    'POST'
                );

                if (response.data.success) {
                    this.freemium.bannerDismissed = true;
                    return { success: true };
                }

                return { success: false };
            } catch (err) {
                console.error('Failed to dismiss banner:', err);
                return { success: false, error: err.message };
            }
        },

        setSorting(direction, column) {
            if (direction) {
                this.sortConfig = { column, direction };
            } else {
                // Toggle direction if same column, otherwise default to asc
                if (this.sortConfig.column === column) {
                    this.sortConfig.direction =
                        this.sortConfig.direction === 'asc' ? 'desc' : 'asc';
                } else {
                    this.sortConfig = { column, direction: 'asc' };
                }
            }
        },

        toggleColumn(columnKey) {
            const column = this.currentColumns.find(
                (col) => col.key === columnKey
            );
            if (column) {
                column.visible = !column.visible;
                this.clearFilters();
            }
        },

        clearFilters() {
            this.selectedCards = [];
            this.selectedUsageTypes = [];
            this.selectedUsers = [];
            this.selectedBanks = [];
            this.selectedPeriodTypes = [];
            this.selectedCategories = [];
            this.searchQuery = '';
        },

        setSearchQuery(query) {
            this.searchQuery = query;
        },

        // Card filter methods
        toggleCardFilter(card) {
            const index = this.selectedCards.findIndex(
                (selected) =>
                    selected.connected_account_id === card.connected_account_id
            );
            if (index > -1) {
                this.selectedCards.splice(index, 1);
            } else {
                this.selectedCards.push(card);
            }
        },

        resetCardFilter() {
            this.selectedCards = [];
        },

        // Usage filter methods
        toggleUsageFilter(usageType) {
            const index = this.selectedUsageTypes.findIndex(
                (selected) => selected.id === usageType.id
            );
            if (index > -1) {
                this.selectedUsageTypes.splice(index, 1);
            } else {
                this.selectedUsageTypes.push(usageType);
            }
        },

        resetUsageFilter() {
            this.selectedUsageTypes = [];
        },

        // User filter methods
        toggleUserFilter(user) {
            const index = this.selectedUsers.findIndex(
                (selected) => selected.id === user.id
            );
            if (index > -1) {
                this.selectedUsers.splice(index, 1);
            } else {
                this.selectedUsers.push(user);
            }
        },

        resetUserFilter() {
            this.selectedUsers = [];
        },

        // Bank filter methods
        toggleBankFilter(bank) {
            const index = this.selectedBanks.findIndex(
                (selected) => selected.id === bank.id
            );
            if (index > -1) {
                this.selectedBanks.splice(index, 1);
            } else {
                this.selectedBanks.push(bank);
            }
        },

        resetBankFilter() {
            this.selectedBanks = [];
        },

        // Period type filter methods
        togglePeriodTypeFilter(periodType) {
            const index = this.selectedPeriodTypes.findIndex(
                (selected) => selected.id === periodType.id
            );
            if (index > -1) {
                this.selectedPeriodTypes.splice(index, 1);
            } else {
                this.selectedPeriodTypes.push(periodType);
            }
        },

        resetPeriodTypeFilter() {
            this.selectedPeriodTypes = [];
        },

        // Category filter methods
        toggleCategoryFilter(category) {
            const idx = this.selectedCategories.findIndex(
                (selected) => selected.id === category.id
            );
            if (idx > -1) {
                this.selectedCategories.splice(idx, 1);
            } else {
                this.selectedCategories.push({
                    id: category.id,
                    name: category.name,
                });
            }
        },

        resetCategoryFilter() {
            this.selectedCategories = [];
        },

        resetCardFilter() {
            this.selectedCards = [];
        },

        setWalletId(walletId) {
            this.walletId = walletId;
        },

        // Navigation actions
        navigateToPreviousBenefit() {
            if (this.isFirstBenefit) return;

            const currentIndex = this.currentBenefitIndex;
            if (currentIndex > 0) {
                const previousBenefit =
                    this.flattenedBenefits[currentIndex - 1];
                this.setSelectedBenefit(previousBenefit);
            }
        },

        navigateToNextBenefit() {
            if (this.isLastBenefit) return;

            const currentIndex = this.currentBenefitIndex;
            if (currentIndex < this.flattenedBenefits.length - 1) {
                const nextBenefit = this.flattenedBenefits[currentIndex + 1];
                this.setSelectedBenefit(nextBenefit);
            }
        },
        setShowLastFour(bool) {
            this.showLastFour = bool;
        },
        setShowInactiveBenefits(bool) {
            this.showInactiveBenefits = bool;
        },
        setShowHiddenBenefits(bool) {
            this.showHiddenBenefits = bool;
            if (this.walletId) {
                ApiService.apiCall(
                    `/wallets/${this.walletId}/display-preferences`,
                    'put',
                    { showHiddenBenefits: bool }
                ).catch((error) => {
                    console.error('Failed to save display preferences:', error);
                });
            }
        },

        async setBenefitVisibility({
            cardBenefitId,
            connectedAccountId,
            isHidden,
        }) {
            if (!this.walletId) {
                throw new Error('Wallet ID is required');
            }
            try {
                await ApiService.apiCall(
                    `/wallets/${this.walletId}/card-benefits/visibility`,
                    'PUT',
                    {
                        card_benefit_id: cardBenefitId,
                        connected_account_id: connectedAccountId,
                        is_hidden: isHidden,
                    }
                );
                // Optimistically reflect the change in local state so the row
                // is filtered (or surfaced) immediately without a full reload.
                const apply = (list) =>
                    list.map((item) => {
                        if (
                            item.benefit?.card_benefit_id === cardBenefitId &&
                            item.connected_account_id === connectedAccountId
                        ) {
                            return {
                                ...item,
                                user_override: {
                                    ...(item.user_override || {}),
                                    is_hidden: isHidden,
                                },
                            };
                        }
                        return item;
                    });
                this.benefits = apply(this.benefits);
                if (this.benefitsByCard?.groups) {
                    this.benefitsByCard = {
                        ...this.benefitsByCard,
                        groups: this.benefitsByCard.groups.map((group) => ({
                            ...group,
                            benefits: apply(group.benefits),
                        })),
                    };
                }
                if (
                    this.selectedBenefit?.benefit?.card_benefit_id ===
                        cardBenefitId &&
                    this.selectedBenefit?.connected_account_id ===
                        connectedAccountId
                ) {
                    this.selectedBenefit = {
                        ...this.selectedBenefit,
                        user_override: {
                            ...(this.selectedBenefit.user_override || {}),
                            is_hidden: isHidden,
                        },
                    };
                }
            } catch (error) {
                console.error('Failed to update benefit visibility', error);
                throw error;
            }
        },

        setShowAddUsageModal(bool) {
            this.showAddUsageModal = bool;
        },

        setShowSelectCardsModal(bool) {
            this.showSelectCardsModal = bool;
        },

        setShowUpgradeModal(bool) {
            this.showUpgradeModal = bool;
        },

        setShowEditUsageModal(bool) {
            this.showEditUsageModal = bool;
        },

        // Sidebar actions
        openBenefitDetails(benefit) {
            this.selectedBenefit = benefit;
            this.sidebarOpen = true;

            if (this.insightsActiveTab === 'insights') {
                this.trackInsightsEvent('button_click', {
                    component: 'benefit_details_sidebar',
                    action: 'opened',
                    card_benefit_id: benefit?.benefit?.card_benefit_id,
                    connected_account_id: benefit?.connected_account_id,
                });
            }
        },

        openBenefitByIds(benefitId, accountId) {
            const match = this.flattenedBenefits.find(
                (b) =>
                    b.benefit?.card_benefit_id === benefitId &&
                    b.connected_account_id === accountId
            );
            if (match && !match.isLocked) {
                this.openBenefitDetails(match);
            }
        },

        closeBenefitDetails() {
            this.sidebarOpen = false;
            this.selectedBenefit = null;

            // Remove deep link query params from URL if present
            const url = new URL(window.location);
            if (
                url.searchParams.has('benefit_id') ||
                url.searchParams.has('account_id')
            ) {
                url.searchParams.delete('benefit_id');
                url.searchParams.delete('account_id');
                window.history.replaceState(
                    {},
                    '',
                    url.pathname + (url.search || '')
                );
            }
        },
        setSelectedBenefit(benefit) {
            this.selectedBenefit = benefit;
        },

        // Helper: Find benefit in the benefits array
        findBenefitIndex(benefitId, connectedAccountId) {
            return this.benefits.findIndex(
                (b) =>
                    b.benefit?.card_benefit_id === benefitId &&
                    b.connected_account_id === connectedAccountId
            );
        },

        // Shared usage submission method for both mobile and desktop
        async handleUsageSubmit(usageData, benefitItem) {
            const benefitId = benefitItem?.benefit?.card_benefit_id;
            const connectedAccountId = benefitItem?.connected_account_id;

            if (!this.walletId || !benefitId || !connectedAccountId) {
                console.error(
                    'Missing benefitId, or connectedAccountId for usage creation.',
                    {
                        walletId: this.walletId,
                        benefitId,
                        connectedAccountId,
                    }
                );
                return false;
            }

            // Find the benefit in the array
            const benefitIndex = this.findBenefitIndex(
                benefitId,
                connectedAccountId
            );
            if (benefitIndex === -1) {
                console.error(
                    'Benefit not found in state for optimistic update'
                );
                return false;
            }

            // Store original benefit and usage summary for rollback
            const originalBenefit = JSON.parse(
                JSON.stringify(this.benefits[benefitIndex])
            );
            const originalUsageSummary = this.benefitUsageSummary
                ? JSON.parse(JSON.stringify(this.benefitUsageSummary))
                : null;

            // Create optimistic usage record
            const tempId = `temp-${generateUUID()}`;
            const isCountBased = this.checkIsCountBased(benefitItem?.benefit);
            const amountNum = isCountBased
                ? 0
                : parseFloat(usageData.amount || 0);
            const countNum = isCountBased ? parseInt(usageData.count || 1) : 1;

            const optimisticUsage = {
                id: tempId,
                uuid: tempId,
                total_amount_used: amountNum,
                usage_count: countNum,
                used_at: usageData.date,
                notes: usageData.notes || '',
                _pending: true,
            };

            // Apply optimistic update - replace entire object to trigger reactivity
            const benefit = this.benefits[benefitIndex];

            // Create updated benefit with new usage record
            const updatedBenefit = {
                ...benefit,
                usage_records: [
                    ...(benefit.usage_records || []),
                    optimisticUsage,
                ],
            };

            // Update benefit with recalculated usage totals
            this.updateBenefitWithRecalculatedUsage(
                benefitIndex,
                updatedBenefit
            );

            // Update usage summary stats optimistically
            if (this.benefitUsageSummary) {
                const newUsedYtd =
                    (this.benefitUsageSummary.used_ytd || 0) + amountNum;
                const totalValue = this.totalAnnualValue;

                this.benefitUsageSummary = {
                    ...this.benefitUsageSummary,
                    used_ytd: newUsedYtd,
                    percent_used:
                        totalValue > 0
                            ? Math.round((newUsedYtd / totalValue) * 100)
                            : 0,
                };
            }

            try {
                const payload = {
                    card_benefit_id: benefitItem.benefit.card_benefit_id,
                    connected_account_id: connectedAccountId,
                    total_amount_used: amountNum,
                    usage_count: countNum,
                    used_at: usageData.date,
                    notes: usageData.notes || '',
                };

                const response = await ApiService.apiCall(
                    `/wallets/${this.walletId}/card-benefits`,
                    'POST',
                    payload
                );

                // Replace optimistic record with real data
                const createdUsage = response?.data?.usage_record;
                if (createdUsage) {
                    const currentBenefit = this.benefits[benefitIndex];
                    const usageIndex = currentBenefit.usage_records.findIndex(
                        (u) => u.id === tempId
                    );
                    if (usageIndex !== -1) {
                        // Replace entire benefit object to trigger reactivity
                        const updatedUsageRecords = [
                            ...currentBenefit.usage_records,
                        ];
                        updatedUsageRecords[usageIndex] = createdUsage;

                        const benefitWithRealRecord = {
                            ...currentBenefit,
                            usage_records: updatedUsageRecords,
                        };

                        // Update benefit with recalculated usage totals
                        this.updateBenefitWithRecalculatedUsage(
                            benefitIndex,
                            benefitWithRealRecord
                        );
                    }
                }

                // Success notification
                notify({
                    group: 'success',
                    title: 'Usage Recorded',
                    text: 'Benefit usage record has been saved successfully',
                    type: 'success',
                });

                // Resync derived aggregates (Insights summaries, charts,
                // per-card rollups) against the authoritative backend totals.
                this.refreshBenefitsSilently();

                return true;
            } catch (e) {
                console.error('Failed to create usage record', e);

                // Rollback optimistic update
                this.rollbackOptimisticBenefit(
                    benefitIndex,
                    originalBenefit,
                    originalUsageSummary
                );

                // Error notification
                notify({
                    group: 'error',
                    title: 'Save Failed',
                    text:
                        e?.response?.data?.message ||
                        'Could not save usage record. Please try again.',
                    type: 'error',
                });

                return false;
            }
        },

        // Submit usage for a sub-benefit (child of a compound benefit)
        async handleSubBenefitUsageSubmit(
            usageData,
            subBenefit,
            connectedAccountId
        ) {
            const subBenefitId = subBenefit?.id;

            if (!this.walletId || !subBenefitId || !connectedAccountId) {
                console.error(
                    'Missing walletId, subBenefitId, or connectedAccountId for sub-benefit usage.',
                    { subBenefitId, connectedAccountId }
                );
                return false;
            }

            const isCountBased = subBenefit.values_summary?.is_count_based;
            const perUnitAmount = parseFloat(subBenefit.amount || 0);
            const isPureCount = isCountBased && perUnitAmount === 0;
            const isHybridCount = isCountBased && perUnitAmount > 0;

            const countNum = isCountBased ? parseInt(usageData.count || 1) : 1;
            const amountNum = isPureCount
                ? 0
                : isHybridCount
                  ? countNum * perUnitAmount
                  : parseFloat(usageData.amount || 0);

            try {
                const payload = {
                    card_benefit_id: subBenefitId,
                    connected_account_id: connectedAccountId,
                    total_amount_used: amountNum,
                    usage_count: countNum,
                    used_at: usageData.date,
                    notes: usageData.notes || '',
                };

                const response = await ApiService.apiCall(
                    `/wallets/${this.walletId}/card-benefits`,
                    'POST',
                    payload
                );

                const createdUsage = response?.data?.usage_record;
                if (createdUsage) {
                    const parentIndex = this.benefits.findIndex(
                        (b) =>
                            b.connected_account_id === connectedAccountId &&
                            b.sub_benefits?.some((s) => s.id === subBenefitId)
                    );

                    if (parentIndex !== -1) {
                        const parent = this.benefits[parentIndex];
                        const updatedSubBenefits = (
                            parent.sub_benefits || []
                        ).map((s) => {
                            if (s.id !== subBenefitId) {
                                return s;
                            }
                            const newUsageRecords = [
                                ...(s.usage_records || []),
                                createdUsage,
                            ];
                            const delta = isPureCount ? countNum : amountNum;
                            return {
                                ...s,
                                usage_records: newUsageRecords,
                                current_period_usage:
                                    (s.current_period_usage || 0) + delta,
                            };
                        });

                        const aggregateUsage = updatedSubBenefits.reduce(
                            (sum, s) =>
                                sum + parseFloat(s.current_period_usage || 0),
                            0
                        );
                        this.benefits[parentIndex] = {
                            ...parent,
                            sub_benefits: updatedSubBenefits,
                            current_period_usage: aggregateUsage,
                            benefit: {
                                ...parent.benefit,
                                current_period_usage: aggregateUsage,
                            },
                        };
                    }
                }

                if (this.benefitUsageSummary && amountNum > 0) {
                    const newUsedYtd =
                        (this.benefitUsageSummary.used_ytd || 0) + amountNum;
                    const totalValue = this.totalAnnualValue;
                    this.benefitUsageSummary = {
                        ...this.benefitUsageSummary,
                        used_ytd: newUsedYtd,
                        percent_used:
                            totalValue > 0
                                ? Math.round((newUsedYtd / totalValue) * 100)
                                : 0,
                    };
                }

                notify({
                    group: 'success',
                    title: 'Usage Recorded',
                    text: 'Benefit usage record has been saved successfully',
                    type: 'success',
                });

                return true;
            } catch (e) {
                console.error('Failed to create sub-benefit usage record', e);
                notify({
                    group: 'error',
                    title: 'Save Failed',
                    text:
                        e?.response?.data?.message ||
                        'Could not save usage record. Please try again.',
                    type: 'error',
                });
                return false;
            }
        },

        // Shared usage update method for editing existing records
        async handleUsageUpdate(usageData, benefitItem, editingEntry) {
            const benefitId = benefitItem?.benefit?.card_benefit_id;
            const connectedAccountId = benefitItem?.connected_account_id;

            if (!this.walletId || !benefitId || !connectedAccountId) {
                console.error(
                    'Missing walletId, benefitId, or connectedAccountId for usage update.',
                    {
                        walletId: this.walletId,
                        benefitId,
                        connectedAccountId,
                    }
                );
                return false;
            }

            if (!editingEntry?.uuid) {
                console.error('UUID is required for updating usage records');
                return false;
            }

            // Find the benefit in the array
            const benefitIndex = this.findBenefitIndex(
                benefitId,
                connectedAccountId
            );
            if (benefitIndex === -1) {
                console.error(
                    'Benefit not found in state for optimistic update'
                );
                return false;
            }

            // Store original benefit and usage summary for rollback
            const originalBenefit = JSON.parse(
                JSON.stringify(this.benefits[benefitIndex])
            );
            const originalUsageSummary = this.benefitUsageSummary
                ? JSON.parse(JSON.stringify(this.benefitUsageSummary))
                : null;

            // Apply optimistic update - replace entire object to trigger reactivity
            const benefit = this.benefits[benefitIndex];

            // Detect owning sub-benefit FIRST — needed to derive the correct isCountBased
            // (a sub-benefit may be count-based under a monetary parent, or vice-versa)
            const owningSubBenefit = (benefit.sub_benefits || []).find((sub) =>
                (sub.usage_records || []).some(
                    (r) => r.uuid === editingEntry.uuid
                )
            );
            const isSubBenefitRecord = !!owningSubBenefit;

            const isCountBasedSource = isSubBenefitRecord
                ? owningSubBenefit
                : benefitItem?.benefit;
            const isCountBased = this.checkIsCountBased(isCountBasedSource);

            const oldAmount = parseFloat(editingEntry.total_amount_used || 0);
            const oldCount = parseInt(editingEntry.usage_count || 1);

            let newAmount = isCountBased ? 0 : parseFloat(usageData.amount || 0);
            let newCount = isCountBased ? parseInt(usageData.count || 1) : 1;

            // Count-based sub-benefits that carry a dollar value per credit
            // (e.g. "$10 – Qty: 2") are recorded as credits × per-credit amount,
            // the same way handleSubBenefitUsageSubmit creates them. Editing one
            // as a single $ use sent usage_count 1 and failed the per-use cap
            // (SUPPORT-930). Forms that only edit the amount (mobile) keep the
            // record's saved credits and the amount typed. When the credits do
            // not change the saved total stays; when they do, the record's own
            // per-credit price is used, since the sub-benefit's amount is the
            // current version's and an older record may predate a price change.
            const currentPerCreditAmount = parseFloat(owningSubBenefit?.amount || 0);
            if (
                isSubBenefitRecord &&
                owningSubBenefit.values_summary?.is_count_based &&
                currentPerCreditAmount > 0
            ) {
                const editedCount = parseInt(usageData.count);
                if (!(editedCount > 0)) {
                    newCount = oldCount;
                    newAmount =
                        usageData.amount !== undefined &&
                        usageData.amount !== null &&
                        usageData.amount !== ''
                            ? parseFloat(usageData.amount)
                            : oldAmount;
                } else if (editedCount === oldCount) {
                    newCount = oldCount;
                    newAmount = oldAmount;
                } else {
                    const recordPerCreditAmount =
                        oldAmount > 0 && oldCount > 0 ? oldAmount / oldCount : 0;
                    newCount = editedCount;
                    newAmount =
                        Math.round(
                            editedCount *
                                (recordPerCreditAmount || currentPerCreditAmount) *
                                100
                        ) / 100;
                }
            }

            const amountDiff = newAmount - oldAmount;

            let updatedBenefit;
            if (isSubBenefitRecord) {
                const updatedSubBenefits = (benefit.sub_benefits || []).map(
                    (sub) => {
                        const hasRecord = (sub.usage_records || []).some(
                            (r) => r.uuid === editingEntry.uuid
                        );
                        if (!hasRecord) return sub;
                        const updatedRecords = sub.usage_records.map((r) =>
                            r.uuid === editingEntry.uuid
                                ? {
                                      ...r,
                                      total_amount_used: newAmount,
                                      usage_count: newCount,
                                      used_at: usageData.date,
                                      notes: usageData.notes || '',
                                  }
                                : r
                        );
                        const subPeriodType = (
                            sub.period_type || 'monthly'
                        ).toLowerCase();
                        const newPeriodUsage = updatedRecords
                            .filter((r) =>
                                this.isDateInCurrentPeriod(
                                    r.used_at,
                                    subPeriodType
                                )
                            )
                            .reduce(
                                (sum, r) =>
                                    sum + parseFloat(r.total_amount_used || 0),
                                0
                            );
                        return {
                            ...sub,
                            usage_records: updatedRecords,
                            current_period_usage: newPeriodUsage,
                        };
                    }
                );
                const aggregateUsage = updatedSubBenefits.reduce(
                    (sum, s) => sum + parseFloat(s.current_period_usage || 0),
                    0
                );
                updatedBenefit = {
                    ...benefit,
                    sub_benefits: updatedSubBenefits,
                    current_period_usage: aggregateUsage,
                    benefit: {
                        ...benefit.benefit,
                        current_period_usage: aggregateUsage,
                    },
                };
                this.benefits[benefitIndex] = updatedBenefit;
            } else {
                // Update usage records
                const updatedUsageRecords = benefit.usage_records.map(
                    (record) =>
                        record.uuid === editingEntry.uuid
                            ? {
                                  ...record,
                                  total_amount_used: newAmount,
                                  usage_count: newCount,
                                  used_at: usageData.date,
                                  notes: usageData.notes || '',
                              }
                            : record
                );

                // Update history_users if it exists
                const updatedHistoryUsers =
                    benefit.benefit?.history_users?.map((record) =>
                        record.uuid === editingEntry.uuid
                            ? {
                                  ...record,
                                  total_amount_used: newAmount,
                                  usage_count: newCount,
                                  used_at: usageData.date,
                                  notes: usageData.notes || '',
                              }
                            : record
                    ) || [];

                updatedBenefit = {
                    ...benefit,
                    usage_records: updatedUsageRecords,
                };

                // Update benefit with recalculated usage totals
                this.updateBenefitWithRecalculatedUsage(
                    benefitIndex,
                    updatedBenefit,
                    { history_users: updatedHistoryUsers }
                );
            }

            // Update usage summary stats optimistically
            if (this.benefitUsageSummary) {
                const newUsedYtd =
                    (this.benefitUsageSummary.used_ytd || 0) + amountDiff;
                const totalValue = this.totalAnnualValue;

                this.benefitUsageSummary = {
                    ...this.benefitUsageSummary,
                    used_ytd: newUsedYtd,
                    percent_used:
                        totalValue > 0
                            ? Math.round((newUsedYtd / totalValue) * 100)
                            : 0,
                };
            }

            try {
                // The server derives benefit/account from the record's uuid and
                // rejects mismatching ids (MY1-2960) — sending benefitId here
                // would break sub-benefit records, whose card_benefit_id is the
                // sub-benefit's, not the parent row's.
                const payload = {
                    total_amount_used: newAmount,
                    usage_count: newCount,
                    used_at: usageData.date,
                    notes: usageData.notes || '',
                    uuid: editingEntry.uuid,
                };

                const response = await ApiService.apiCall(
                    `/wallets/${this.walletId}/card-benefits/update`,
                    'PUT',
                    payload
                );

                // Success notification
                notify({
                    group: 'success',
                    title: 'Usage Updated',
                    text: 'Benefit usage record has been updated successfully',
                    type: 'success',
                });

                // Resync derived aggregates against the authoritative
                // backend totals after an optimistic update.
                this.refreshBenefitsSilently();

                return true;
            } catch (e) {
                console.error('Failed to update usage record', e);

                // Rollback optimistic update
                this.rollbackOptimisticBenefit(
                    benefitIndex,
                    originalBenefit,
                    originalUsageSummary
                );

                // Error notification
                notify({
                    group: 'error',
                    title: 'Update Failed',
                    text:
                        e?.response?.data?.message ||
                        'Could not update usage record. Please try again.',
                    type: 'error',
                });

                return false;
            }
        },

        calculateMonthlyValue(benefit) {
            const valueDetails = benefit.benefit.value_details || {};
            const count = parseFloat(valueDetails.count) || 1;
            const baseAmount = parseFloat(benefit.benefit.amount) || 0;

            // Check for current month override
            const overrideMonth = valueDetails.override_month?.month;
            const currentMonth = dayjs().format('MM');

            if (
                overrideMonth === currentMonth &&
                valueDetails.override_month?.value != null
            ) {
                return (
                    (parseFloat(valueDetails.override_month.value) || 0) * count
                );
            }

            // Apply count multiplier if benefit has count tracking AND a monetary amount
            // This handles benefits like "2x $50 credits" where we want to show $100
            // For count-only benefits (amount = 0), just return 0
            const hasCountMultiplier =
                benefit.benefit.values_summary?.is_count_based &&
                baseAmount > 0;
            return hasCountMultiplier ? baseAmount * count : baseAmount;
        },
        calculateDisplayAmount(benefit) {
            const periodType = benefit.benefit.period_type?.toLowerCase();
            const valueDetails = benefit.benefit.value_details || {};
            const isAfterFirstYear = this.isAccountPastFirstYear(
                benefit.account
            );
            const eligibleAfterFirstAnniversary =
                valueDetails.eligible_after_first_anniversary || false;

            if (!isAfterFirstYear && eligibleAfterFirstAnniversary) {
                return 0;
            }

            if (periodType === 'monthly') {
                return this.calculateMonthlyValue(benefit);
            }

            const multiYearPeriods = [
                'biennial',
                'triennial',
                'quadrennial',
                'quinquennial',
            ];
            if (multiYearPeriods.includes(periodType)) {
                const resetBasis = benefit.benefit?.reset_basis;

                if (resetBasis === 'usage') {
                    // For usage-based multi-year benefits, the backend returns null for
                    // period_end_date when the ineligibility window has expired and the
                    // user is eligible to redeem again. Show the benefit amount in that case.
                    const periodEndDate = benefit.period_end_date;
                    const isCurrentlyIneligible =
                        periodEndDate && dayjs(periodEndDate).isAfter(dayjs());
                    return isCurrentlyIneligible
                        ? 0
                        : parseFloat(benefit.benefit.amount) || 0;
                }

                const { start: ytdStart, end: ytdEnd } =
                    this.periodBoundaries.annual;
                if (this.isBenefitActiveThisYear(benefit, ytdStart, ytdEnd)) {
                    return parseFloat(benefit.benefit.amount) || 0;
                }
                return 0;
            }

            if (benefit.benefit.values_summary?.is_count_based) {
                const count =
                    parseFloat(benefit.benefit.value_details?.count) || 1;
                const amount = parseFloat(benefit.benefit.amount) || 0;

                if (amount > 0) {
                    return count * amount;
                }
                return count;
            }

            const firstYearValue =
                valueDetails.value_first_year != null
                    ? parseFloat(valueDetails.value_first_year)
                    : null;
            const afterFirstYearValue =
                valueDetails.value_after_first_year != null
                    ? parseFloat(valueDetails.value_after_first_year)
                    : null;

            if (!isAfterFirstYear && firstYearValue != null) {
                return firstYearValue;
            }

            if (isAfterFirstYear && afterFirstYearValue != null) {
                return afterFirstYearValue;
            }

            // Handle tiered benefits
            if (
                benefit.benefit.value_details?.tiers &&
                benefit.benefit.value_details.tiers.length > 0
            ) {
                const stackingMode =
                    benefit.benefit.value_details.tier_stacking_mode ||
                    'sequential';

                const summedTierValues =
                    benefit.benefit.value_details.tiers.reduce(
                        (total, tier) => total + (parseFloat(tier.value) || 0),
                        0
                    );

                if (stackingMode === 'concurrent') {
                    // Concurrent: sum all tier values
                    return summedTierValues;
                } else {
                    // Sequential: return cumulative value of all unlocked tiers
                    // If usage is 0, show tier 1 value
                    // If usage >= tier 1, show tier 1 + tier 2 value
                    // etc.
                    const usage = benefit.current_period_usage || 0;
                    let cumulativeThreshold = 0;
                    let cumulativeValue = 0;

                    for (const tier of benefit.benefit.value_details.tiers) {
                        const tierValue = parseFloat(tier.value) || 0;
                        cumulativeValue += tierValue;
                        cumulativeThreshold += tierValue;

                        // If usage hasn't reached this tier's threshold, return cumulative value so far
                        if (usage < cumulativeThreshold) {
                            return cumulativeValue;
                        }
                    }

                    // All tiers exhausted - reuse summedTierValues
                    return summedTierValues;
                }
            }

            // Default: return base amount
            return parseFloat(benefit.benefit.amount) || 0;
        },

        /**
         * Check if a multi-year benefit is active this year
         * Multi-year benefits should only be included if:
         * - They were used THIS YEAR, OR
         * - They have NOT been used at all yet
         */
        isBenefitActiveThisYear(benefitItem, ytdStart, ytdEnd) {
            const benefit = benefitItem.benefit;
            if (!benefit) return true;

            const periodType = benefit.period_type?.toUpperCase();
            const multiYearPeriods = [
                'BIENNIAL',
                'TRIENNIAL',
                'QUADRENNIAL',
                'QUINQUENNIAL',
            ];

            // If not a multi-year benefit, always include it
            if (!multiYearPeriods.includes(periodType)) {
                return true;
            }

            // For multi-year benefits, check usage history
            const usageRecords = benefitItem.usage_records || [];

            // No usage records? Benefit is available this year
            if (usageRecords.length === 0) {
                return true;
            }

            // Check if ANY usage was in the current year
            const hasCurrentYearUsage = usageRecords.some((record) => {
                const usedAt = dayjs(record.used_at);
                return (
                    usedAt.isSameOrAfter(ytdStart) &&
                    usedAt.isSameOrBefore(ytdEnd)
                );
            });

            // Include if used this year, exclude if only used in previous years
            return hasCurrentYearUsage;
        },

        /**
         * Determine if an account is past its first year
         */
        isAccountPastFirstYear(account) {
            if (!account) return true; // Conservative default

            const openedAt = account.actual_date_opened;
            if (!openedAt) return true; // Conservative default

            const openDate = dayjs(openedAt);
            const oneYearAgo = dayjs().subtract(1, 'year');

            return openDate.isSameOrBefore(oneYearAgo);
        },

        /**
         * Calculate annual value for a single benefit
         * Handles monthly overrides and all period types
         */
        calculateAnnualValue(benefitItem) {
            const benefit = benefitItem.benefit;
            if (!benefit) return 0;

            const displayAmount = this.calculateDisplayAmount(benefitItem);

            const count = parseFloat(benefit.value_details?.count) || 1;

            if (
                benefit.period_type?.toUpperCase() === 'MONTHLY' &&
                benefit.value_details?.override_month
            ) {
                const baseAmount = parseFloat(benefit.amount) || 0;
                const overrideValue =
                    parseFloat(benefit.value_details.override_month.value) || 0;

                return baseAmount * count * 11 + overrideValue * count;
            }

            // Convert to annual value based on period type
            return this.convertPeriodValueToAnnual(
                displayAmount,
                benefit.period_type,
                count
            );
        },

        /**
         * Calculate prorated annual value for a specific year.
         * For monthly/statement benefits:
         *   - Current year: cap at current month
         *   - Card open year: start from open month
         *   - Full past years: return full annual value
         * For other period types: returns full annual value unchanged.
         *
         * @param {Object} benefitItem
         * @param {string|number} year
         * @returns {number}
         */
        calculateProratedAnnualValue(benefitItem, year) {
            const benefit = benefitItem.benefit;
            if (!benefit) return 0;

            const periodType = (benefit.period_type || '')
                .toLowerCase()
                .replace(/[_-]/g, '');

            // Only prorate monthly/statement period types
            if (periodType !== 'monthly' && periodType !== 'statement') {
                return this.calculateAnnualValue(benefitItem);
            }

            const currentDate = dayjs();
            const currentYear = currentDate.year();
            const yearNum = parseInt(year);

            // Future year — no value
            if (yearNum > currentYear) {
                return 0;
            }

            // Determine start month (1-based)
            let startMonth = 1;
            const accountOpenDate = benefitItem.account?.actual_date_opened;
            if (accountOpenDate) {
                const openDate = dayjs(accountOpenDate);
                if (openDate.year() === yearNum) {
                    startMonth = openDate.month() + 1; // dayjs months are 0-based
                }
            }

            // Determine end month (1-based)
            let endMonth = 12;
            if (yearNum === currentYear) {
                endMonth = currentDate.month() + 1; // dayjs months are 0-based
            }

            const totalMonths = Math.max(0, endMonth - startMonth + 1);
            if (totalMonths === 0) return 0;

            // Full past year — return full annual value (no prorating needed)
            if (startMonth === 1 && endMonth === 12) {
                return this.calculateAnnualValue(benefitItem);
            }

            const count = parseFloat(benefit.value_details?.count) || 1;
            const baseAmount = parseFloat(benefit.amount) || 0;
            const overrideMonth = benefit.value_details?.override_month;

            if (overrideMonth) {
                const overrideMonthNum = parseInt(overrideMonth.month);
                const overrideValue = parseFloat(overrideMonth.value) || 0;

                if (
                    overrideMonthNum >= startMonth &&
                    overrideMonthNum <= endMonth
                ) {
                    // Override month is within the prorated range
                    return (
                        (baseAmount * (totalMonths - 1) + overrideValue) * count
                    );
                }
            }

            return baseAmount * totalMonths * count;
        },

        /**
         * Convert a period value to annual value based on period type
         */
        convertPeriodValueToAnnual(periodValue, periodType) {
            if (!periodType) return periodValue;

            // Normalize: lowercase and remove underscores/hyphens
            const normalizedType = periodType
                .toLowerCase()
                .replace(/[_-]/g, '');

            switch (normalizedType) {
                case 'monthly':
                    return periodValue * 12;
                case 'quarterly':
                    return periodValue * 4;
                case 'semiannual':
                case 'biannual':
                    return periodValue * 2;
                case 'annual':
                    return periodValue;
                case 'biennial':
                case 'triennial':
                case 'quadrennial':
                case 'quinquennial':
                    return periodValue; // Multi-year - use as-is
                case 'statement':
                    return periodValue * 12; // Assume monthly
                default:
                    return periodValue;
            }
        },

        // Shared usage deletion method
        /**
         * MY1-3080 — open the "Add from My Spending" picker for a benefit.
         * `remainingAmount` caps what a linked transaction may record.
         */
        openTransactionPicker(benefit, remainingAmount = 0) {
            this.transactionPicker = {
                open: true,
                benefit,
                remainingAmount: Math.max(0, Number(remainingAmount) || 0),
            };
        },

        closeTransactionPicker() {
            this.transactionPicker = {
                open: false,
                benefit: null,
                remainingAmount: 0,
            };
        },

        /**
         * MY1-3080 — record a My Spending transaction as usage of a benefit
         * (the API stamps it `user_linked`), then pull the canonical benefit
         * state. Resolves to null on success, or the message to show.
         */
        async linkTransactionToBenefit(benefit, transaction, amount) {
            const benefitId = benefit?.benefit?.card_benefit_id;

            if (!benefitId || !transaction?.id) {
                return 'Select a transaction first';
            }

            try {
                await ApiService.apiCall(
                    `/transactions/${encodeURIComponent(transaction.id)}/link-benefit`,
                    'post',
                    {
                        card_benefit_id: benefitId,
                        amount: Number(amount),
                    }
                );
            } catch (error) {
                const message =
                    error?.response?.data?.message ||
                    Object.values(error?.response?.data?.errors || {}).flat()[0] ||
                    'Could not add this usage. Please try again.';

                notify({
                    group: 'error',
                    title: 'Add usage failed',
                    text: message,
                    type: 'error',
                });

                return message;
            }

            notify({
                group: 'success',
                title: 'Usage added',
                text: `${transaction.merchant_name || 'Transaction'} added to ${
                    benefit?.benefit?.benefit_title || 'this benefit'
                }`,
                type: 'success',
            });

            await this.refreshBenefitsSilently();

            return null;
        },

        /**
         * MY1-3082 — hide the connect-bank banner for one issuer, everywhere
         * it would show (every benefit on every card from that bank), and
         * persist it the same way the wallet banner does. Optimistic: a
         * failed save keeps it hidden for the session and retries on the
         * next dismissal.
         */
        async dismissConnectInstitution(institutionCode) {
            if (!institutionCode) {
                return false;
            }

            if (!this.dismissedConnectInstitutions.includes(institutionCode)) {
                this.dismissedConnectInstitutions.push(institutionCode);
            }

            try {
                await window.axios.post(
                    `/connect-institutions/${encodeURIComponent(institutionCode)}/dismiss`
                );

                return true;
            } catch (error) {
                console.error('Failed to persist banner dismissal', error);

                return false;
            }
        },

        dismissSpendingPrompt() {
            this.spendingPromptDismissed = true;
        },

        /**
         * MY1-3083 — turn My Spending on in place for the signed-in user
         * (the wallet's MY1-3077 pattern), then refetch so every benefit on a
         * connected card flips to its auto-tracked state. Callers must only
         * offer this on the viewer's own cards: the endpoint mutates the
         * signed-in user, never the card's owner.
         */
        async enableSpending() {
            if (this.spendingOptInBusy) {
                return false;
            }

            this.spendingOptInBusy = true;

            try {
                await window.axios.post('/spending/enable');
            } catch (error) {
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text:
                            error?.response?.data?.message ||
                            'Failed to turn on My Spending',
                    },
                    4000
                );

                return false;
            } finally {
                this.spendingOptInBusy = false;
            }

            // Past this point the preference is committed; a refresh failure
            // is a stale-view problem, not a failed opt-in.
            notify(
                {
                    group: 'success',
                    title: 'Success',
                    text: "My Spending is on — we'll track your benefits automatically",
                },
                4000
            );

            // refreshBenefitsSilently re-points the open sidebar's selectedBenefit
            // at the fresh row (fetchBenefits would leave it stale and clear the
            // list on failure) and reports the outcome instead of throwing.
            const refreshed = await this.refreshBenefitsSilently();

            if (!refreshed) {
                notify(
                    {
                        group: 'warn',
                        title: 'Heads up',
                        text: 'My Spending is on, but this page could not refresh. Reload to see the change.',
                    },
                    6000
                );
            }

            return true;
        },

        // MY1-3081: `feedback` is the optional "What's wrong?" answer for an
        // auto-tracked record; the API stores it with the rejection so the
        // matcher never recreates the pairing.
        async handleUsageDelete(benefitItem, entryToDelete, feedback = null) {
            const benefitId = benefitItem?.benefit?.card_benefit_id;
            const connectedAccountId = benefitItem?.connected_account_id;

            if (!this.walletId || !benefitId || !connectedAccountId) {
                console.error('Missing required IDs for usage deletion');
                return false;
            }

            const entryUuid = entryToDelete?.uuid || entryToDelete?.id;
            if (!entryUuid) {
                console.error(
                    'UUID or ID is required for deleting usage records'
                );
                return false;
            }

            // Find the benefit in the array
            const benefitIndex = this.findBenefitIndex(
                benefitId,
                connectedAccountId
            );
            if (benefitIndex === -1) {
                console.error(
                    'Benefit not found in state for optimistic delete'
                );
                return false;
            }

            // Store original benefit and usage summary for rollback
            const originalBenefit = JSON.parse(
                JSON.stringify(this.benefits[benefitIndex])
            );
            const originalUsageSummary = this.benefitUsageSummary
                ? JSON.parse(JSON.stringify(this.benefitUsageSummary))
                : null;

            const deletedAmount = parseFloat(
                entryToDelete.total_amount_used || 0
            );

            // Apply optimistic delete - replace entire object to trigger reactivity
            const benefit = this.benefits[benefitIndex];

            // Check whether the entry lives on the parent or on a sub-benefit
            const isSubBenefitRecord = (benefit.sub_benefits || []).some(
                (sub) =>
                    (sub.usage_records || []).some(
                        (r) => (r.uuid || r.id) === entryUuid
                    )
            );

            let updatedBenefit;

            if (isSubBenefitRecord) {
                // Remove the record from the matching sub-benefit and recalculate its usage
                const updatedSubBenefits = (benefit.sub_benefits || []).map(
                    (sub) => {
                        const hasRecord = (sub.usage_records || []).some(
                            (r) => (r.uuid || r.id) === entryUuid
                        );
                        if (!hasRecord) return sub;

                        const updatedRecords = sub.usage_records.filter(
                            (r) => (r.uuid || r.id) !== entryUuid
                        );
                        const subPeriodType = (
                            sub.period_type || 'monthly'
                        ).toLowerCase();
                        const newUsage = updatedRecords
                            .filter((r) =>
                                this.isDateInCurrentPeriod(
                                    r.used_at,
                                    subPeriodType
                                )
                            )
                            .reduce(
                                (sum, r) =>
                                    sum + parseFloat(r.total_amount_used || 0),
                                0
                            );
                        return {
                            ...sub,
                            usage_records: updatedRecords,
                            current_period_usage: newUsage,
                        };
                    }
                );

                updatedBenefit = {
                    ...benefit,
                    sub_benefits: updatedSubBenefits,
                };
            } else {
                // Direct parent usage record
                const updatedUsageRecords = benefit.usage_records.filter(
                    (record) => (record.uuid || record.id) !== entryUuid
                );
                updatedBenefit = {
                    ...benefit,
                    usage_records: updatedUsageRecords,
                };
            }

            // Recalculate current period usage from remaining records
            const recalculatedUsage =
                this.recalculateCurrentPeriodUsage(updatedBenefit);

            this.benefits[benefitIndex] = {
                ...updatedBenefit,
                current_period_usage: recalculatedUsage.current_period_usage,
                count_used_current_period:
                    recalculatedUsage.count_used_current_period,
                benefit: {
                    ...benefit.benefit,
                    current_period_usage:
                        recalculatedUsage.current_period_usage,
                    count_used_current_period:
                        recalculatedUsage.count_used_current_period,
                    history_users:
                        benefit.benefit?.history_users?.filter(
                            (record) => (record.uuid || record.id) !== entryUuid
                        ) || [],
                },
            };

            // Update usage summary stats optimistically
            if (this.benefitUsageSummary) {
                const newUsedYtd = Math.max(
                    0,
                    (this.benefitUsageSummary.used_ytd || 0) - deletedAmount
                );
                const totalValue = this.totalAnnualValue;

                this.benefitUsageSummary = {
                    ...this.benefitUsageSummary,
                    used_ytd: newUsedYtd,
                    percent_used:
                        totalValue > 0
                            ? Math.round((newUsedYtd / totalValue) * 100)
                            : 0,
                };
            }

            try {
                const trimmedFeedback =
                    typeof feedback === 'string' ? feedback.trim() : '';

                const response = await ApiService.apiCall(
                    `/wallets/${this.walletId}/card-benefits/${entryUuid}`,
                    'delete',
                    trimmedFeedback ? { feedback: trimmedFeedback } : {}
                );

                // Success notification
                notify({
                    group: 'success',
                    title: 'Usage Deleted',
                    text: 'Benefit usage record has been deleted successfully',
                    type: 'success',
                });

                // Resync derived aggregates against the authoritative
                // backend totals after an optimistic delete.
                this.refreshBenefitsSilently();

                return true;
            } catch (e) {
                console.error('Failed to delete usage record', e);

                // Rollback optimistic delete
                this.rollbackOptimisticBenefit(
                    benefitIndex,
                    originalBenefit,
                    originalUsageSummary
                );

                // Error notification
                notify({
                    group: 'error',
                    title: 'Delete Failed',
                    text: 'Could not delete the usage entry. Please try again.',
                    type: 'error',
                });

                return false;
            }
        },

        /**
         * Check if a date falls within a specific period
         * @param {Date|string} date - The date to check
         * @param {string} periodType - The period type (monthly, quarterly, annual, etc.)
         * @returns {boolean}
         */
        isDateInCurrentPeriod(date, periodType) {
            const checkDate = dayjs(date);
            const periodType_lower = periodType
                ?.toLowerCase()
                .replace(/[_-]/g, '');
            const boundaries = this.periodBoundaries[periodType_lower];

            if (!boundaries) {
                // Default to annual if period type not found
                const annualBoundaries = this.periodBoundaries.annual;
                return (
                    checkDate.isSameOrAfter(annualBoundaries.start) &&
                    checkDate.isSameOrBefore(annualBoundaries.end)
                );
            }

            return (
                checkDate.isSameOrAfter(boundaries.start) &&
                checkDate.isSameOrBefore(boundaries.end)
            );
        },

        /**
         * Get anniversary-based period boundaries using the card opening date
         * Mirrors backend logic from CardBenefitUsageRecordService::getAnniversaryBasedPeriod
         * @param {string} periodType - The period type (monthly, quarterly, annual, etc.)
         * @param {string|Date} cardOpenedAt - The date the card was opened
         * @returns {Object|null} - { start: Date, end: Date } or null if no card opened date
         */
        getAnniversaryPeriodBoundaries(periodType, cardOpenedAt) {
            if (!cardOpenedAt) {
                return null;
            }

            const cardOpenDate = dayjs(cardOpenedAt);
            if (!cardOpenDate.isValid()) {
                return null;
            }

            const now = dayjs();
            const periodTypeLower = (periodType || 'annual')
                .toLowerCase()
                .replace(/[_-]/g, '');

            // Determine period duration in months for shorter periods
            let periodMonths = 0;
            switch (periodTypeLower) {
                case 'monthly':
                    periodMonths = 1;
                    break;
                case 'quarterly':
                    periodMonths = 3;
                    break;
                case 'semiannual':
                case 'biannual':
                    periodMonths = 6;
                    break;
            }

            // Determine period duration in years for longer periods
            let periodYears = 1;
            switch (periodTypeLower) {
                case 'quadrennial':
                    periodYears = 4;
                    break;
                case 'quinquennial':
                    periodYears = 5;
                    break;
                case 'triennial':
                    periodYears = 3;
                    break;
                case 'biennial':
                    periodYears = 2;
                    break;
                case 'decennial':
                    periodYears = 10;
                    break;
                case 'annual':
                case 'yearly':
                default:
                    periodYears = 1;
                    break;
            }

            // Calculate how many complete periods have passed since card opened
            if (periodMonths > 0) {
                const monthsElapsed = now.diff(cardOpenDate, 'month');
                const periodsPassed = Math.floor(monthsElapsed / periodMonths);

                return {
                    start: cardOpenDate
                        .add(periodsPassed * periodMonths, 'month')
                        .toDate(),
                    end: cardOpenDate
                        .add((periodsPassed + 1) * periodMonths, 'month')
                        .subtract(1, 'day')
                        .endOf('day')
                        .toDate(),
                };
            } else {
                const yearsElapsed = now.diff(cardOpenDate, 'year');
                const periodsPassed = Math.floor(yearsElapsed / periodYears);

                return {
                    start: cardOpenDate
                        .add(periodsPassed * periodYears, 'year')
                        .toDate(),
                    end: cardOpenDate
                        .add((periodsPassed + 1) * periodYears, 'year')
                        .subtract(1, 'day')
                        .endOf('day')
                        .toDate(),
                };
            }
        },

        /**
         * Recalculate current period usage from usage records
         * Filters records by period and sums the appropriate field (count or amount)
         * Respects reset_basis field for anniversary-based period calculations
         * @param {Object} benefit - The benefit object with usage_records
         * @returns {Object} - { current_period_usage, count_used_current_period }
         */
        recalculateCurrentPeriodUsage(benefit) {
            // For compound benefits, aggregate from sub-benefit current_period_usage values
            // (sub-benefits maintain their own per-period recalculation)
            if ((benefit?.sub_benefits?.length ?? 0) > 0) {
                const aggregateUsage = benefit.sub_benefits.reduce(
                    (sum, s) => sum + parseFloat(s.current_period_usage || 0),
                    0
                );
                return {
                    current_period_usage: aggregateUsage,
                    count_used_current_period: 0,
                };
            }

            // Get period type - handle both nested and direct structures
            let periodType = benefit?.benefit?.period_type;
            if (!periodType && benefit?.period_type) {
                periodType = benefit.period_type;
            }

            // Convert to lowercase and handle undefined
            const periodTypeLower = periodType?.toLowerCase() || 'annual';

            const usageRecords = benefit?.usage_records || [];
            const isCountBased = this.checkIsCountBased(benefit?.benefit);

            // Check reset_basis to determine period calculation method
            const resetBasis = benefit?.benefit?.reset_basis;
            const accountOpenDate = benefit?.account?.actual_date_opened;

            let currentPeriodRecords;

            if (resetBasis === 'card_anniversary' && accountOpenDate) {
                // Use anniversary-based period calculation
                const anniversaryBoundaries =
                    this.getAnniversaryPeriodBoundaries(
                        periodTypeLower,
                        accountOpenDate
                    );

                if (anniversaryBoundaries) {
                    currentPeriodRecords = usageRecords.filter((record) => {
                        const usedAt = dayjs(record.used_at);
                        return (
                            usedAt.isSameOrAfter(anniversaryBoundaries.start) &&
                            usedAt.isSameOrBefore(anniversaryBoundaries.end)
                        );
                    });
                } else {
                    // Fall back to calendar-based if anniversary calculation fails
                    currentPeriodRecords = usageRecords.filter((record) =>
                        this.isDateInCurrentPeriod(
                            record.used_at,
                            periodTypeLower
                        )
                    );
                }
            } else {
                // Use calendar-based period calculation (existing behavior)
                currentPeriodRecords = usageRecords.filter((record) =>
                    this.isDateInCurrentPeriod(record.used_at, periodTypeLower)
                );
            }

            // Sum usage counts (always needed)
            const countUsed = currentPeriodRecords.reduce(
                (total, record) =>
                    total + (parseFloat(record.usage_count) || 0),
                0
            );

            // For count-based benefits, usage is the count; otherwise sum amounts
            const amountUsed = isCountBased
                ? countUsed
                : currentPeriodRecords.reduce(
                      (total, record) =>
                          total + (parseFloat(record.total_amount_used) || 0),
                      0
                  );

            return {
                current_period_usage: amountUsed,
                count_used_current_period: countUsed,
            };
        },

        /**
         * Calculate annual usage for a benefit from its usage records
         * Returns the total amount used in the current calendar year
         * For count-based benefits, returns the count; for monetary benefits, returns the amount
         */
        calculateAnnualUsage(benefit) {
            const ownRecords = benefit?.usage_records || [];
            const subRecords = (benefit?.sub_benefits || []).flatMap(
                (s) => s.usage_records || []
            );
            // For compound benefits use only sub-benefit records — parent records predate the split
            const usageRecords =
                subRecords.length > 0 ? subRecords : ownRecords;
            const { start: ytdStart, end: ytdEnd } =
                this.periodBoundaries.annual;

            // Check if this is a count-based benefit
            const isCountBased = this.checkIsCountBased(benefit?.benefit);

            return usageRecords.reduce((total, record) => {
                const usedAt = dayjs(record.used_at);
                if (
                    usedAt.isSameOrAfter(ytdStart) &&
                    usedAt.isSameOrBefore(ytdEnd)
                ) {
                    if (isCountBased) {
                        return total + (parseFloat(record.usage_count) || 0);
                    } else {
                        return (
                            total + (parseFloat(record.total_amount_used) || 0)
                        );
                    }
                }
                return total;
            }, 0);
        },

        /**
         * Calculate the annual limit for a benefit based on its period type
         * For count-based benefits, returns the count limit
         * For monetary benefits, returns the amount limit
         * Monthly: limit * 12, Quarterly: limit * 4, Semiannual: limit * 2, Annual: limit * 1
         */
        calculateAnnualLimit(benefit) {
            const periodType = benefit?.benefit?.period_type
                ?.toLowerCase()
                .replace(/[_-]/g, '');
            const isCountBased = this.checkIsCountBased(benefit?.benefit);

            // For count-based benefits, use the count value
            let benefitLimit;
            if (isCountBased) {
                benefitLimit =
                    parseFloat(benefit?.benefit?.value_details?.count) || 0;
            } else {
                benefitLimit = this.calculateDisplayAmount(benefit);
            }

            // Multiply by period frequency to get annual limit
            if (periodType === 'monthly') {
                return benefitLimit * 12;
            } else if (periodType === 'quarterly') {
                return benefitLimit * 4;
            } else if (
                periodType === 'semiannual' ||
                periodType === 'biannual'
            ) {
                return benefitLimit * 2;
            }

            return benefitLimit;
        },

        /**
         * Check if a benefit is fully redeemed for the year/period
         * For anniversary-based benefits, compares current period usage against period limit
         * For calendar-based benefits, compares annual usage against annual limit
         */
        isBenefitFullyRedeemedThisYear(benefit) {
            if (!benefit) return false;

            const resetBasis = benefit?.benefit?.reset_basis;
            const accountOpenDate = benefit?.account?.actual_date_opened;

            // For anniversary-based benefits, use current period calculation
            if (resetBasis === 'card_anniversary' && accountOpenDate) {
                const recalculated =
                    this.recalculateCurrentPeriodUsage(benefit);
                const currentPeriodUsage =
                    recalculated.current_period_usage || 0;

                const isCountBased = this.checkIsCountBased(benefit?.benefit);

                let periodLimit;
                if (isCountBased) {
                    periodLimit =
                        parseFloat(benefit?.benefit?.value_details?.count) || 0;
                } else {
                    periodLimit = this.calculateDisplayAmount(benefit);
                }

                return periodLimit > 0 && currentPeriodUsage >= periodLimit;
            }

            // For calendar-based benefits, use annual calculation (existing behavior)
            const annualUsage = this.calculateAnnualUsage(benefit);
            const annualLimit = this.calculateAnnualLimit(benefit);

            return annualLimit > 0 && annualUsage >= annualLimit;
        },

        /**
         * Update benefit with recalculated usage totals
         * Helper to avoid duplication when updating benefits with new usage records
         */
        updateBenefitWithRecalculatedUsage(
            benefitIndex,
            updatedBenefit,
            additionalBenefitFields = {}
        ) {
            const benefit = this.benefits[benefitIndex];
            const recalculatedUsage =
                this.recalculateCurrentPeriodUsage(updatedBenefit);

            const newBenefit = {
                ...updatedBenefit,
                current_period_usage: recalculatedUsage.current_period_usage,
                count_used_current_period:
                    recalculatedUsage.count_used_current_period,
                benefit: {
                    ...benefit.benefit,
                    ...additionalBenefitFields,
                    current_period_usage:
                        recalculatedUsage.current_period_usage,
                    count_used_current_period:
                        recalculatedUsage.count_used_current_period,
                },
            };

            this.benefits[benefitIndex] = newBenefit;

            // Also update selectedBenefit if it's the same benefit (for sidebar reactivity)
            if (
                this.selectedBenefit &&
                this.selectedBenefit.benefit?.card_benefit_id ===
                    benefit.benefit?.card_benefit_id &&
                this.selectedBenefit.connected_account_id ===
                    benefit.connected_account_id
            ) {
                this.selectedBenefit = newBenefit;
            }
        },

        /**
         * Revert a benefit to a pre-optimistic snapshot after a failed
         * create/update/delete request. Restores the array slot, the usage
         * summary, and — mirroring updateBenefitWithRecalculatedUsage — the
         * open details sidebar's selectedBenefit when it points at the same
         * benefit. Without the selectedBenefit restore, a rejected record
         * stays visible in the sidebar's history until the next refresh.
         */
        rollbackOptimisticBenefit(
            benefitIndex,
            originalBenefit,
            originalUsageSummary
        ) {
            this.benefits[benefitIndex] = originalBenefit;

            if (
                this.selectedBenefit &&
                this.selectedBenefit.benefit?.card_benefit_id ===
                    originalBenefit.benefit?.card_benefit_id &&
                this.selectedBenefit.connected_account_id ===
                    originalBenefit.connected_account_id
            ) {
                this.selectedBenefit = originalBenefit;
            }

            if (originalUsageSummary) {
                this.benefitUsageSummary = originalUsageSummary;
            }
        },

        /**
         * Check if a benefit is fully redeemed for the current period
         * Compares current period usage against period limit
         */
        isBenefitFullyRedeemedThisPeriod(benefit) {
            if (!benefit) return false;

            const isCountBased = this.checkIsCountBased(benefit?.benefit);

            // Get current period usage
            const currentPeriodUsage = benefit.current_period_usage || 0;

            // Get period limit using existing method that handles all edge cases
            // (tiers, monthly overrides, first year values, count multipliers)
            let periodLimit;
            if (isCountBased) {
                periodLimit =
                    parseFloat(benefit?.benefit?.value_details?.count) || 0;
            } else {
                periodLimit = this.calculateDisplayAmount(benefit);
            }

            return periodLimit > 0 && currentPeriodUsage >= periodLimit;
        },

        /**
         * Save a user override for a benefit (e.g., custom renewal date)
         */
        async saveUserOverride(overrideData) {
            if (!this.walletId) {
                console.error('Missing walletId for saving user override');
                return false;
            }

            try {
                await ApiService.apiCall(
                    `/wallets/${this.walletId}/card-benefits/user-override`,
                    'POST',
                    overrideData
                );

                // Refetch benefits to get updated data
                await this.fetchBenefits(this.walletId);

                notify(
                    {
                        group: 'success',
                        title: 'Custom renewal date saved',
                    },
                    4000
                );

                return true;
            } catch (error) {
                console.error('Failed to save user override:', error);
                notify(
                    {
                        group: 'error',
                        title: 'Failed to save custom renewal date',
                        text:
                            error.response?.data?.message ||
                            'An error occurred',
                    },
                    4000
                );
                return false;
            }
        },

        /**
         * Delete a user override for a benefit
         */
        async deleteUserOverride(overrideData) {
            if (!this.walletId) {
                console.error('Missing walletId for deleting user override');
                return false;
            }

            try {
                await ApiService.apiCall(
                    `/wallets/${this.walletId}/card-benefits/user-override`,
                    'DELETE',
                    overrideData
                );

                // Refetch benefits to get updated data
                await this.fetchBenefits(this.walletId);

                notify(
                    {
                        group: 'success',
                        title: 'Custom renewal date removed',
                    },
                    4000
                );

                return true;
            } catch (error) {
                console.error('Failed to delete user override:', error);
                notify(
                    {
                        group: 'error',
                        title: 'Failed to remove custom renewal date',
                        text:
                            error.response?.data?.message ||
                            'An error occurred',
                    },
                    4000
                );
                return false;
            }
        },

        // ========================================================
        // Insights tab actions (MY1-2046)
        // ========================================================
        trackInsightsEvent(eventType, metadata = {}) {
            if (!this.walletId) return;
            ApiService.apiCall('/analytics/track', 'POST', {
                events: [
                    {
                        event_type: eventType,
                        data: {
                            feature: 'benefits_insights',
                            surface: 'insights_tab',
                            insights_range_preset: this.insightsRangePreset,
                            insights_shift_count: this.insightsRangeShiftCount,
                            insights_group_by: this.insightsGroupBy,
                            insights_show_card_numbers:
                                this.insightsShowCardNumbers,
                            ...metadata,
                        },
                    },
                ],
            }).catch((error) => {
                console.error('Failed to track insights event:', error);
            });
        },

        trackInsightsFilterChange(filterName, values = []) {
            clearTimeout(_filterDebounceTimers[filterName]);
            _filterDebounceTimers[filterName] = setTimeout(() => {
                this.trackInsightsEvent('filter_applied', {
                    filter_name: filterName,
                    selected_count: values.length,
                    selected_values: values,
                });
            }, 600);
        },

        setInsightsActiveTab(tab) {
            const fromTab = this.insightsActiveTab;
            this.insightsActiveTab = tab;
            if (tab === 'insights' || fromTab === 'insights') {
                this.trackInsightsEvent('tab_switch', {
                    from_tab: fromTab,
                    to_tab: tab,
                });
            }
        },

        setInsightsRangePreset(preset) {
            this.insightsRangePreset = preset;
            this.insightsRangeShiftCount = 0;
            if (preset !== 'custom') {
                this.insightsCustomRange = null;
            }
            this.trackInsightsEvent('filter_applied', {
                filter_name: 'range_preset',
                preset,
            });
        },

        setInsightsCustomRange({ startYear, startMonth, endYear, endMonth }) {
            this.insightsRangePreset = 'custom';
            this.insightsRangeShiftCount = 0;
            this.insightsCustomRange = {
                startYear,
                startMonth,
                endYear,
                endMonth,
            };
            this.trackInsightsEvent('filter_applied', {
                filter_name: 'range_custom',
                start_year: startYear,
                start_month: startMonth + 1,
                end_year: endYear,
                end_month: endMonth + 1,
            });
        },

        /**
         * Shift the date window by one preset-length step.
         * `direction` = -1 moves into the past, +1 toward the present.
         * Forward shifts clamp at 0 (cannot move past "today"). Backward shifts
         * clamp at MAX_INSIGHTS_BACKWARD_SHIFT for the active preset family —
         * roughly 10 years into the past — to prevent unbounded navigation
         * into a range where the user has no data.
         */
        shiftInsightsRange(direction) {
            const max = this._maxInsightsBackwardShift();
            const next = this.insightsRangeShiftCount - direction;
            this.insightsRangeShiftCount = Math.min(Math.max(0, next), max);
            this.trackInsightsEvent('button_click', {
                button: direction < 0 ? 'range_previous' : 'range_next',
                preset: this.insightsRangePreset,
                shift_count: this.insightsRangeShiftCount,
            });
        },

        /**
         * Maximum backward shift count for the active preset. Targets a
         * ~10-year horizon translated into the preset's step unit.
         */
        _maxInsightsBackwardShift() {
            const preset = this.insightsRangePreset;
            if (preset === 'this-year' || preset === 'previous-year') {
                return 10;
            }
            if (preset === 'this-month' || preset === 'previous-month') {
                return 120;
            }
            if (preset === 'custom' && this.insightsCustomRange) {
                const { startYear, startMonth, endYear, endMonth } =
                    this.insightsCustomRange;
                const start = dayjs(new Date(startYear, startMonth, 1));
                const end = dayjs(new Date(endYear, endMonth, 1));
                const lengthMonths = Math.max(1, end.diff(start, 'month') + 1);
                return Math.max(1, Math.ceil(120 / lengthMonths));
            }
            return 10;
        },

        toggleInsightsCardFilter(card) {
            const idx = this.insightsSelectedCards.findIndex(
                (c) => c.connected_account_id === card.connected_account_id
            );
            if (idx >= 0) {
                this.insightsSelectedCards.splice(idx, 1);
            } else {
                this.insightsSelectedCards.push({
                    connected_account_id: card.connected_account_id,
                    title: card.title,
                });
            }
            this.trackInsightsFilterChange(
                'cards',
                this.insightsSelectedCards.map((c) => c.connected_account_id)
            );
        },

        resetInsightsCardFilter() {
            this.insightsSelectedCards = [];
            this.trackInsightsFilterChange('cards', []);
        },

        toggleInsightsUserFilter(user) {
            const idx = this.insightsSelectedUsers.findIndex(
                (u) => u.id === user.id
            );
            if (idx >= 0) {
                this.insightsSelectedUsers.splice(idx, 1);
            } else {
                this.insightsSelectedUsers.push({
                    id: user.id,
                    name: user.name,
                });
            }
            this.trackInsightsFilterChange(
                'users',
                this.insightsSelectedUsers.map((u) => u.id)
            );
        },

        resetInsightsUserFilter() {
            this.insightsSelectedUsers = [];
            this.trackInsightsFilterChange('users', []);
        },

        toggleInsightsBankFilter(bank) {
            const idx = this.insightsSelectedBanks.findIndex(
                (b) => b.id === bank.id
            );
            if (idx >= 0) {
                this.insightsSelectedBanks.splice(idx, 1);
            } else {
                this.insightsSelectedBanks.push({
                    id: bank.id,
                    name: bank.name,
                });
            }
            this.trackInsightsFilterChange(
                'banks',
                this.insightsSelectedBanks.map((b) => b.id)
            );
        },

        resetInsightsBankFilter() {
            this.insightsSelectedBanks = [];
            this.trackInsightsFilterChange('banks', []);
        },

        toggleInsightsPeriodFilter(period) {
            const idx = this.insightsSelectedPeriodTypes.findIndex(
                (p) => p.id === period.id
            );
            if (idx >= 0) {
                this.insightsSelectedPeriodTypes.splice(idx, 1);
            } else {
                this.insightsSelectedPeriodTypes.push({
                    id: period.id,
                    name: period.name,
                });
            }
            this.trackInsightsFilterChange(
                'period_types',
                this.insightsSelectedPeriodTypes.map((p) => p.id)
            );
        },

        resetInsightsPeriodFilter() {
            this.insightsSelectedPeriodTypes = [];
            this.trackInsightsFilterChange('period_types', []);
        },

        toggleInsightsUsageFilter(option) {
            const idx = this.insightsSelectedUsage.findIndex(
                (u) => u.id === option.id
            );
            if (idx >= 0) {
                this.insightsSelectedUsage.splice(idx, 1);
            } else {
                this.insightsSelectedUsage.push({
                    id: option.id,
                    name: option.name,
                });
            }
            this.trackInsightsFilterChange(
                'usage',
                this.insightsSelectedUsage.map((u) => u.id)
            );
        },

        resetInsightsUsageFilter() {
            this.insightsSelectedUsage = [];
            this.trackInsightsFilterChange('usage', []);
        },

        toggleInsightsCategoryFilter(category) {
            const idx = this.insightsSelectedCategories.findIndex(
                (c) => c.id === category.id
            );
            if (idx >= 0) {
                this.insightsSelectedCategories.splice(idx, 1);
            } else {
                this.insightsSelectedCategories.push({
                    id: category.id,
                    name: category.name,
                });
            }
            this.trackInsightsFilterChange(
                'categories',
                this.insightsSelectedCategories.map((c) => c.id)
            );
        },

        resetInsightsCategoryFilter() {
            this.insightsSelectedCategories = [];
            this.trackInsightsFilterChange('categories', []);
        },

        clearInsightsFilters() {
            this.insightsSelectedCards = [];
            this.insightsSelectedBanks = [];
            this.insightsSelectedPeriodTypes = [];
            this.insightsSelectedUsage = [];
            this.insightsSelectedCategories = [];
            this.insightsSelectedUsers = [];
            this.trackInsightsEvent('filter_applied', {
                filter_name: 'clear_all',
            });
        },

        setInsightsGroupBy(mode) {
            this.insightsGroupBy = mode === 'by_card' ? 'by_card' : 'flat';
            this.trackInsightsEvent('filter_applied', {
                filter_name: 'group_by',
                value: this.insightsGroupBy,
            });
        },

        setInsightsShowCardNumbers(value) {
            this.insightsShowCardNumbers = !!value;
            this.trackInsightsEvent('filter_applied', {
                filter_name: 'show_card_numbers',
                value: this.insightsShowCardNumbers,
            });
        },

        setInsightsSortBy(value) {
            this.insightsSortBy = value || 'benefit_asc';
            this.trackInsightsEvent('filter_applied', {
                filter_name: 'sort',
                value: this.insightsSortBy,
            });
        },

        setUsageByCardExpanded(value) {
            this.usageByCardExpanded = !!value;
            this.trackInsightsEvent('button_click', {
                component: 'usage_by_card',
                action: this.usageByCardExpanded ? 'expand' : 'collapse',
            });
        },

        applyInsightsSort(rows) {
            const sortBy = this.insightsSortBy || 'benefit_asc';
            if (sortBy === 'benefit_asc') {
                return [...rows].sort((a, b) =>
                    (a.name || '').localeCompare(b.name || '')
                );
            }
            // Split on the LAST underscore so multi-word field names like
            // `total_value_desc` parse correctly (`field='total_value'`,
            // `direction='desc'`).
            const lastUnderscore = sortBy.lastIndexOf('_');
            const field =
                lastUnderscore > -1 ? sortBy.slice(0, lastUnderscore) : sortBy;
            const direction =
                lastUnderscore > -1 ? sortBy.slice(lastUnderscore + 1) : 'asc';
            const dir = direction === 'desc' ? -1 : 1;
            return [...rows].sort((a, b) => {
                const av = Number(a[field] ?? 0);
                const bv = Number(b[field] ?? 0);
                if (av === bv) {
                    return (a.name || '').localeCompare(b.name || '');
                }
                return (av - bv) * dir;
            });
        },
    },
});
